/**
 * ── THE DISPLAY AXIS: whether a metric can be drawn as a CHART or a MAP ─────────────
 *
 * A card on this page is a **(metric x display x dimension)** triple. `display` is the
 * axis this file answers for: Metric (one figure), Chart (a plot over time), Map (a
 * geographic plot) — the same three values `ANALYTICS_CARD_CATEGORIES` already
 * enumerates, reused rather than re-declared, because a second enum meaning the same
 * thing is the drift working-conventions §44 exists to prevent.
 *
 * ## CAPABILITY IS DERIVED. It is never a flag anyone sets on a metric.
 *
 * This is the load-bearing rule and it is written down because THIS EPIC HAS SHIPPED
 * THE SAME DEFECT THREE TIMES — an affordance offered where the data could not honour
 * it:
 *
 *   - the ROI legend row that toggled on and drew nothing, every bucket being `null`;
 *   - a metric reported `needs_input` when no input could ever unblock it, so the form
 *     it sent the brand to changed nothing;
 *   - `$0` rendered for a spend that was in fact UNKNOWN.
 *
 * A draggable "show as chart" on a metric with no series would be the fourth, and it is
 * the one most likely to be believed, because the failure is SILENT: an empty chart
 * reads as "no data in this range" rather than as "this was never possible".
 *
 * A hardcoded list of the two ids that happen to work today is the same forbidden flag
 * spelled differently — it would go stale in the direction that lies (a metric gains a
 * series, the list does not, so the affordance stays hidden; or worse, a series is
 * removed and the list still offers it). So every answer below rests on a REAL,
 * TYPE-LEVEL PROPERTY of the data path that would have to draw the thing:
 *
 * | question | the property it rests on |
 * |---|---|
 * | is there a series at all? | a numeric field on `GetAnalyticsStatisticsData`, the dated bucket row |
 * | can the chart draw that dimension? | the `dataType` union `ScansByTime2` accepts |
 * | do the events carry a location? | `IScanMapPoint`'s `geo_lat` / `geo_lng` |
 * | can the map draw that dimension? | a real scans-map request that draws it — `mode`, or a pinned `scan_statuses` |
 *
 * Each is enforced by a TOTAL `Record` over a mapped type, not by a hand-kept list. Add
 * a dated count to the statistics response, or a third value to `AnalyticsDataType`, and
 * this file stops compiling until the new case is classified. That is the acceptance
 * criterion "a metric added to the registry gets a correct value with no extra
 * annotation", discharged by the compiler rather than by anyone remembering.
 *
 * ## The honest consequence: MOST METRICS ARE INCAPABLE, and that is correct
 *
 * Vera stores current STATE, not events (`VRG-362`), so the great majority of what this
 * page shows has no history to plot. `mkt_geo_reach` is a count of countries as of now;
 * the ROI money metrics are a figure computed for a window, not a series. Reporting
 * those as incapable is the feature working, not a gap in it.
 *
 * ## Why no reason string ever implies an action
 *
 * §46 splits an unavailable control by WHO OWNS THE RESOLUTION: a permission block stays
 * visible and disabled with a tooltip naming what to ask for, because someone else
 * resolves it. Capability is resolved by NOBODY the user can reach — it is a property of
 * what Vera records. So the sentences below say what is not recorded and stop there.
 * "Ask an admin" would be a false promise, and a false promise on a dashboard is exactly
 * how a brand learns to stop trusting the numbers beside it.
 */
import type {
  ICountryMetricRow,
  IScanMapPoint,
  RoiValueGranularity,
  TopInfluencerRankKey,
} from '@repo/types'

import type {
  AnalyticsDataType,
  AnalyticsSeriesCountField,
  AnalyticsSeriesDimension,
  AnalyticsValueDimension,
  GetAnalyticsTotalsCounts,
} from 'store/analytics/types'

import {
  ANALYTICS_CARD_CATEGORIES,
  SCANS_BY_COUNTRY_CARD_ID,
  analyticsVariantCardId,
  parseAnalyticsVariantCardId,
  type AnalyticsCardCategory,
} from './analyticsGrid'
import { ANALYTICS_TILE_IDS } from './analyticsViewSeeds'

/**
 * The display axis IS the card-category axis.
 *
 * `ANALYTICS_CARD_CATEGORIES` already describes FORM alone — how a card is DRAWN — after
 * an earlier `KPI / ROI / Chart / Map` set was rejected for mixing two independent axes.
 * That makes it exactly the vocabulary this file needs, so it is aliased rather than
 * copied: the glyphs, the labels and the library's type filter all keep reading one
 * table.
 */
export type AnalyticsDisplay = AnalyticsCardCategory

/** Metric, then Chart, then Map, then Table — the registry's authored order. */
export const ANALYTICS_DISPLAY_ORDER: AnalyticsDisplay[] = [
  'metric',
  'chart',
  'map',
  'table',
]

export const analyticsDisplayLabel = (display: AnalyticsDisplay): string =>
  ANALYTICS_CARD_CATEGORIES[display].label

/**
 * Whether one display is available for one metric, and — when it is not — the sentence
 * that says why.
 *
 * `reason` is REQUIRED on the incapable branch, and the discriminated union is what
 * enforces it. §46's forcing function, applied to capability: a disabled affordance that
 * cannot say why is not a disabled affordance, it is a dead control. If you cannot write
 * the sentence, the answer is not "incapable" and the derivation above is wrong.
 */
export type DisplayCapability = { capable: true } | { capable: false; reason: string }

/* ────────────────────────────────────────────────────────────────────────────────────
   1. IS THERE A SERIES AT ALL?
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * The dated count fields of one statistics bucket, derived from the response TYPE.
 *
 * Declared in `store/analytics/types` beside the response it is derived from, rather than
 * here, because `ScansByTime2` needs the same derivation to type its own field lookups —
 * and two mapped types over one response is exactly the drift §36 is about.
 */
type SeriesCountField = AnalyticsSeriesCountField

/**
 * ⚠️ TOTAL over `SeriesCountField`, which is the whole point.
 *
 * Add a dated count to the statistics response and this object is a compile error until
 * the new field is listed, at which point its dimension becomes chartable with no other
 * edit anywhere. Remove one and the stale key is an error here rather than an affordance
 * that offers a series the server stopped sending.
 *
 * A `Set` built from a literal array would give the same runtime answer and none of that
 * — it would happily go stale in both directions.
 */
const SERIES_COUNT_FIELDS: Record<SeriesCountField, true> = {
  scans_count: true,
  certificates_count: true,
  transfers_count: true,
  // VERA-260 — the four the ROI cards already showed as a single figure. Each is a real
  // dated count on every bucket, so each is now a real series.
  invalid_scans_count: true,
  stolen_reports_count: true,
  lost_reports_count: true,
  resales_count: true,
}

/** `scans_count` -> `scans`. The dimension is the field minus its `_count` suffix. */
const stripCount = (field: string): string => field.replace(/_count$/, '')

/** Dimensions the sink can bucket over time. Derived, never listed. */
const SERIES_DIMENSIONS = new Set<string>(Object.keys(SERIES_COUNT_FIELDS).map(stripCount))

/* ────────────────────────────────────────────────────────────────────────────────────
   1b. THE SECOND SINK — A VALUE PER BUCKET (VERA-260)
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * ── WHY RATES, MEANS AND MEDIANS NEEDED A SECOND PATH AT ALL ────────────────────────
 *
 * `SERIES_COUNT_FIELDS` above is total over the `*_count` members of the bucket row, so
 * until now "chartable" and "is a count" were the same predicate. That was not a
 * limitation of the classification — it was a limitation of the SINK, which returned
 * nothing else. The granular endpoint now also returns six `AnalyticsBucketStatistic`s
 * (value + numerator + denominator per bucket), and this section is the second half of
 * the same derivation: a statistic added to that response widens `AnalyticsValueDimension`
 * by itself and every total record below stops compiling until it is classified.
 *
 * 🚨 THE THREE THINGS A VALUE SERIES MUST DECLARE, and none of them has a safe default:
 *
 *   UNIT            a count shares one y-axis with every other count; a percentage, a
 *                   money mean and a median in DAYS share it with nothing. Plotting them
 *                   on the count axis is the "dollars and counts on one scale" hazard
 *                   `ScansByTimeProps.composedSeries` already names.
 *   RECONSTRUCTION  how the series relates to the card above it — see below. This is the
 *                   one that decides what the surface SAYS, and getting it wrong produces
 *                   a chart that looks like the card is broken.
 *   LABEL           a value series is titled for the STATISTIC, not for a thing counted,
 *                   so `stripCount`'s dimension-naming does not apply.
 */

/**
 * ── HOW A PER-BUCKET SERIES RELATES TO THE CARD ABOVE IT ────────────────────────────
 *
 * 🚨 A PER-BUCKET RATE DOES NOT AGGREGATE TO ITS CARD, AND THAT IS CORRECT. Counts sum;
 * means and rates do not. The overall invalid rate is `Σinvalid / Σscans` — NOT the
 * average of the daily rates, which differ whenever daily volume varies. So the sum
 * invariant this file's count half depends on ("each series sums to its own ROI headline")
 * cannot simply be restated here, and asserting it would be the error.
 *
 * But "does not sum" is not one relationship, it is three, and they are far apart in how
 * badly a reader can be misled. So the difference is CLASSIFIED rather than left to a
 * blanket caveat, and the classification is what the chart's description and tooltip are
 * generated from.
 *
 * ## The three, with the numbers each was verified against (Louis Vuitton, all time)
 *
 * | class | means | measured |
 * |---|---|---|
 * | `ratio-of-sums` | the card IS `Σnumerator / Σdenominator` over these buckets — exactly recoverable, the buckets are a true decomposition | invalid scan rate summed to **269 of 6453**, the card's own terms, exactly; average resale price to **169100 cents over 18**, likewise |
 * | `partitioned-population` | every observation falls in exactly one bucket, so the series is built over precisely the card's population — but the STATISTIC does not recombine, because a median of medians is not a median | time to first scan: **37 observations** across the buckets, **37** in the card |
 * | `bucket-local` | the denominator is a DISTINCT count over an entity that SPANS buckets, so summing overshoots and the per-bucket figure answers a different question | items per owner: **26 of 12** across buckets against the card's **26 of 8** (an owner active in two buckets is two owners here); resales per resold item **18 of 18** against **18 of 7** |
 *
 * ## ⚠️ `repeat_scan_rate` IS THE EXTREME CASE AND IT IS WORTH KNOWING THE NUMBER
 *
 * Measured on the same data: the CARD reads **100.0%** (21 of 21 certificates scanned more
 * than once over the range) while the daily buckets read **0.0%** (0 of 447 — not one
 * certificate was scanned twice within a single day). Same predicate, same table, same
 * filters; the only difference is the window, and the window IS part of the measurement.
 *
 * That is not a defect in either figure and it is not fixable by arithmetic. It is why
 * this metric's series is labelled for what the BUCKET measures ("Repeat scans in bucket")
 * rather than inheriting the card's title, and why `bucket-local` carries the strongest of
 * the three sentences. A chart titled "Repeat scan rate" reading a flat 0% under a card
 * reading 100% is precisely the plausible-looking falsehood this file exists to refuse.
 *
 * ⚠️ IF THAT PAIRING IS JUDGED UNACCEPTABLE, THE FIX IS TO WITHHOLD THE DISPLAY, NOT TO
 * SOFTEN THE SENTENCE — set this metric to `false` in a capability record and say why.
 * That is a product decision about what a brand should be shown, not a technical one, so
 * it is surfaced here rather than made silently.
 */
export type AnalyticsValueReconstruction =
  | 'ratio-of-sums'
  | 'partitioned-population'
  | 'bucket-local'

const VALUE_RECONSTRUCTION: Record<
  AnalyticsValueDimension,
  AnalyticsValueReconstruction
> = {
  invalid_scan_rate: 'ratio-of-sums',
  avg_resale_price_cents: 'ratio-of-sums',
  median_days_to_first_scan: 'partitioned-population',
  certificates_per_owner: 'bucket-local',
  resales_per_resold_item: 'bucket-local',
  repeat_scan_rate: 'bucket-local',
}

/**
 * What the surface says about the relationship, per class.
 *
 * Written as complete sentences a reader can act on rather than a badge, for the reason
 * §46 gives about disabled controls: an indicator that cannot say what it means is not an
 * indicator. Each names the arithmetic, because "this does not match the card" without a
 * reason reads as a bug report about the page.
 */
export const ANALYTICS_VALUE_RECONSTRUCTION_NOTE: Record<
  AnalyticsValueReconstruction,
  string
> = {
  'ratio-of-sums':
    'Each point is this period alone. The figure above is not their average — it is the total of every numerator over the total of every denominator, so busier periods weigh more.',
  'partitioned-population':
    'Each point is the median for that period alone. Medians cannot be combined, so the figure above is the median over the whole range rather than an average of these points.',
  'bucket-local':
    'Each point is measured WITHIN its own period, so this is not a breakdown of the figure above — the same item or person counted once there can be counted in several periods here. Read the trend, not the level.',
}

export const analyticsValueReconstruction = (
  dimension: AnalyticsValueDimension,
): AnalyticsValueReconstruction => VALUE_RECONSTRUCTION[dimension]

/** The axis a value dimension is measured on. Decides formatting and the y-domain. */
export type AnalyticsValueUnit = 'percent' | 'cents' | 'days' | 'ratio'

/**
 * ⚠️ TOTAL, and the reason it cannot default: a statistic with no declared unit would be
 * drawn on whatever axis it landed next to. `allowDecimals={false}` alone would render
 * every mean between 0 and 1 as a flat zero line.
 */
const VALUE_UNIT: Record<AnalyticsValueDimension, AnalyticsValueUnit> = {
  invalid_scan_rate: 'percent',
  repeat_scan_rate: 'percent',
  avg_resale_price_cents: 'cents',
  median_days_to_first_scan: 'days',
  certificates_per_owner: 'ratio',
  resales_per_resold_item: 'ratio',
}

export const analyticsValueUnit = (
  dimension: AnalyticsValueDimension,
): AnalyticsValueUnit => VALUE_UNIT[dimension]

/**
 * The series name in the legend and the tooltip.
 *
 * ⚠️ DELIBERATELY NOT THE CARD'S TITLE for the two `bucket-local` rates. "Repeat scans in
 * bucket" rather than "Repeat scan rate" is the whole mitigation for the 100%-vs-0% case
 * documented above: the series says what it measured, so the chart never claims to be a
 * decomposition of the number above it. The count half of this file already establishes
 * that a series name is allowed to differ from its card title (see
 * `DIMENSION_SERIES_LABEL` — "Reported stolen" the card, "Stolen reports" the line); here
 * that latitude is doing real work rather than saving a word.
 */
const VALUE_SERIES_LABEL: Record<AnalyticsValueDimension, string> = {
  invalid_scan_rate: 'Invalid scan rate',
  repeat_scan_rate: 'Repeat scans in period',
  avg_resale_price_cents: 'Average resale price',
  median_days_to_first_scan: 'Median days to first scan',
  certificates_per_owner: 'Items per owner this period',
  resales_per_resold_item: 'Resales per item this period',
}

export const analyticsValueLabel = (dimension: AnalyticsValueDimension): string =>
  VALUE_SERIES_LABEL[dimension]

/**
 * What the tooltip calls the two terms. `null` for a median, which has no numerator.
 *
 * The tooltip prints "3 invalid of 47 scans", never a bare "6.4%" — the same rule the
 * choropleth applies, and for the same reason: a percentage without its denominator is
 * unreadable, and the reader cannot tell 1-of-2 from 400-of-800.
 */
const VALUE_TERM_NOUNS: Record<
  AnalyticsValueDimension,
  { numerator: string | null; denominator: string }
> = {
  invalid_scan_rate: { numerator: 'invalid', denominator: 'scans' },
  repeat_scan_rate: { numerator: 'repeat-scanned', denominator: 'certificates scanned' },
  avg_resale_price_cents: { numerator: null, denominator: 'priced resales' },
  median_days_to_first_scan: { numerator: null, denominator: 'certificates' },
  certificates_per_owner: { numerator: 'items', denominator: 'owners' },
  resales_per_resold_item: { numerator: 'resales', denominator: 'items' },
}

export const analyticsValueTerms = (dimension: AnalyticsValueDimension) =>
  VALUE_TERM_NOUNS[dimension]

/**
 * ── THE MINIMUM DENOMINATOR A POINT IS PLOTTED ON ───────────────────────────────────
 *
 * 🚨 A SMALL PER-BUCKET DENOMINATOR IS FAR NOISIER THAN THE AGGREGATE, and the noise does
 * not merely add error — it OWNS THE AXIS. One invalid scan out of two plots at 50% on a
 * series whose real level is 4%, so the y-domain stretches to accommodate a point that
 * rests on two observations and every real movement flattens into the bottom eighth of the
 * chart. This is the same failure the scan choropleth solved, and it is solved the same
 * way: suppress the point rather than annotate it.
 *
 * ## Why 10 and not the choropleth's 30
 *
 * The choropleth aggregates a COUNTRY over the WHOLE RANGE, so 30 is a low bar there — a
 * country with fewer than 30 scans in three months is genuinely not measured. A bucket is
 * a slice of that same data, so the same threshold would erase most of the series and the
 * suppression would stop being informative.
 *
 * 10 is where a single event stops dominating: at n = 10 one event moves a proportion by
 * 10 percentage points, which is already larger than most of the differences a reader is
 * looking for; below it, the point is reporting the event rather than the rate. Stated as
 * the reasoning rather than a citation because that is what it is — a judgement about
 * legibility, not a significance test, and it is the number to revisit if the suppression
 * proves too aggressive on real brands.
 *
 * ## ⚠️ IT IS DELIBERATELY NOT SCALED BY BUCKET GRANULARITY
 *
 * The obvious refinement — a lower threshold for hourly buckets — was rejected. It would
 * make a point's disappearance depend on a control (the date range, which picks the
 * granularity) that a reader has no reason to connect to it, and it would mean the same
 * data supports a rate at one zoom level and not at another with nothing saying why. A
 * fixed threshold handles the interaction correctly instead: hourly buckets have small
 * denominators, so most of them ARE suppressed, and that is the honest answer — an hour
 * with four scans cannot support a rate no matter how the chart is configured.
 *
 * ## ⚠️ SUPPRESSED IS NOT EMPTY, AND THE SURFACE MUST NOT COLLAPSE THEM
 *
 * Both render as a gap in the line, so on their own they are indistinguishable — which
 * would be a fresh instance of the very defect the null-not-zero rule exists to prevent,
 * one level up. That is why the server sends `denominator` even when it sends no value,
 * and why the tooltip renders a suppressed bucket EXPLICITLY ("not shown — 4 scans, needs
 * 10") rather than omitting the row. `denominator === 0` says nothing happened; a non-zero
 * denominator under this threshold says something happened and was too little to plot.
 */
export const ANALYTICS_MIN_STATISTIC_DENOMINATOR = 10

/**
 * Whether this point is plotted. `false` covers BOTH "undefined" and "too little", and the
 * caller is expected to tell them apart from `denominator` — see the note above.
 */
export const isPlottableStatistic = (
  statistic: { value: number | null; denominator: number } | undefined,
): boolean =>
  statistic != null &&
  statistic.value !== null &&
  statistic.denominator >= ANALYTICS_MIN_STATISTIC_DENOMINATOR

/**
 * ── THE AUTHORED HALF FOR VALUES: ROI CARD -> THE STATISTIC IT SHOWS ────────────────
 *
 * Same standard as `ROI_CARD_DIMENSION` below, and for the same reason: matching on title
 * is forbidden, so the link is authored by someone who can assert that the card and the
 * bucket statistic are computed over the SAME POPULATION with the same predicates. Each
 * was checked against the SQL on both sides — `getGranularByRange`'s new CTEs name their
 * `getRoiMetrics` counterpart individually.
 *
 * | card | bucket statistic | counterpart CTE |
 * |---|---|---|
 * | `mkt_invalid_scan_rate` | `invalid_scan_rate` | `invalid_scan_total` |
 * | `mkt_repeat_scan_rate` | `repeat_scan_rate` | `repeat_scan_rate_total` |
 * | `mkt_time_to_first_scan` | `median_days_to_first_scan` | `first_scan_total` |
 * | `log_avg_resale_price` | `avg_resale_price_cents` | `resale_total.avg_price` |
 * | `log_items_per_owner` | `certificates_per_owner` | `owner_holdings_total` |
 * | `log_resales_per_item` | `resales_per_resold_item` | `resale_total` |
 *
 * ⚠️ WHAT REMAINS ABSENT, and it is still not "not done yet". `legal_transfer_completion_rate`,
 * `log_avg_transfer_time`, `log_time_to_first_resale` and `log_product_lifespan` are all
 * computable per bucket by exactly this machinery — each is anchored on a dated event that
 * partitions cleanly (`transfers.completed_at`, `products.manufactured_at` -> last scan).
 * They are omitted because their `getRoiMetrics` CTEs are being changed in parallel
 * (VRG-491 / VRG-497), and a series authored against a definition that is mid-move is how
 * a chart comes to disagree with its own card. They slot in with one CTE and one row here
 * each, once that settles.
 */
const VALUE_CARD_DIMENSION: Record<string, AnalyticsValueDimension> = {
  mkt_invalid_scan_rate: 'invalid_scan_rate',
  mkt_repeat_scan_rate: 'repeat_scan_rate',
  mkt_time_to_first_scan: 'median_days_to_first_scan',
  log_avg_resale_price: 'avg_resale_price_cents',
  log_items_per_owner: 'certificates_per_owner',
  log_resales_per_item: 'resales_per_resold_item',
}

/** The statistic this card plots over time, or `null` when it plots a count or nothing. */
export const analyticsCardValueDimension = (
  cardId: string,
): AnalyticsValueDimension | null => VALUE_CARD_DIMENSION[cardId] ?? null

/* ────────────────────────────────────────────────────────────────────────────────────
   2. CAN THE RENDERER DRAW THAT DIMENSION?
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * ⚠️ THE SINK HAVING A SERIES IS NOT ENOUGH — the renderer has to have an accessor.
 *
 * This record used to be the place that answered "no" for `transfers`, and the reason it
 * gave was a real one: `ScansByTime2` resolved its plotted series through
 * `dataType === 'certificates' ? 'certificates_detailed' : 'scans_detailed'`, so no
 * accessor would ever have read `transfers_detailed`, and offering the affordance would
 * have drawn a SCANS series under a transfers title — worse than drawing nothing.
 *
 * VERA-260 CLOSED THAT, and did it by fixing the renderer rather than by widening this
 * record's meaning. `ScansByTime2` now resolves both its total field and its (optional)
 * per-status breakdown through `Record`s keyed on `AnalyticsSeriesDimension`, so every
 * dimension the sink buckets has an accessor by construction. Hence every value below is
 * `true` — not because the check was relaxed, but because the thing it was checking for
 * was built.
 *
 * ⚠️ THE RECORD STAYS, and it is not ceremony. Chartability is still the CONJUNCTION of
 * "the sink can answer it" and "the renderer can plot it", and the two are still allowed
 * to disagree — a future count whose unit is money, say, cannot honestly share this
 * chart's single y-axis (see `ScansByTimeProps.composedSeries`' own note on scaling) and
 * would be entered here as `false` with the reason below saying so.
 *
 * Keyed on `AnalyticsSeriesDimension` — DERIVED from the statistics response — so a new
 * dated count is a compile error here until someone says whether the chart can draw it.
 */
const CHART_RENDERABLE: Record<AnalyticsSeriesDimension, boolean> = {
  scans: true,
  certificates: true,
  transfers: true,
  invalid_scans: true,
  stolen_reports: true,
  lost_reports: true,
  resales: true,
}

/**
 * ⚠️ TYPE-LEVEL ASSERTION, not a comment: the map's row type must carry coordinates.
 *
 * Everything below rests on the scans-map endpoint returning located points. If
 * `geo_lat` / `geo_lng` are ever removed or made nullable on `IScanMapPoint`, this
 * declaration stops compiling — which is the correct place to find out, rather than on a
 * map that renders with nothing on it. That is the "events carry a location" property in
 * its enforceable form.
 */
type MapSinkIsLocated = IScanMapPoint extends { geo_lat: number; geo_lng: number }
  ? true
  : 'IScanMapPoint no longer carries coordinates — mappability below is unfounded'
const MAP_SINK_IS_LOCATED: MapSinkIsLocated = true
void MAP_SINK_IS_LOCATED

/**
 * Dimensions the map can plot, given a located sink.
 *
 * ⚠️ KEYED ON THE WIDE UNION SINCE VERA-260, and the previous narrow key is exactly what
 * this record was getting wrong. It used to be `Record<AnalyticsDataType, boolean>` — the
 * two-member union the scans-map endpoint's `mode` happens to name — which made the
 * question it answers UNASKABLE for every other dimension: `invalid_scans` is located
 * (`MAP_LOCATED` has said so since the dimension existed) and fell through to "this map
 * cannot yet plot them" with no entry anywhere saying whether that was a decision or an
 * accident. It was an accident.
 *
 * `AnalyticsDataType` is NOT widened to fix it, and that is deliberate: it is also the key
 * of `LEGACY_PANEL_METRIC_ID`, which would then have to answer "which legacy panel was
 * Invalid Scans" — a question with no answer, since no such panel ever existed. Two unions
 * with different jobs, exactly as `store/analytics/types` describes them.
 *
 * The record is still NOT redundant with `MAP_LOCATED` beside it. That one asks whether the
 * events carry coordinates (a fact about the data model); this asks whether THIS CARD can
 * issue a request for them and title itself correctly (a fact about our code). The three
 * `true`s below each rest on a real query:
 *
 * | dimension | the request that draws it |
 * |---|---|
 * | `scans` | `mode: 'all'` — every located scan |
 * | `certificates` | `mode: 'latest'` — one point per certificate |
 * | `invalid_scans` | `scan_statuses: ['invalid']` — the rejected subset, already wired |
 *
 * The `false`s are unreachable in practice (`MAP_LOCATED` short-circuits first, so a
 * transfer never gets this far) and are written as `false` rather than `true` because that
 * is the honest answer: there is no query on this card that would draw them.
 *
 * ⚠️ THIS RECORD ANSWERS FOR THE **PIN** GROUPING ONLY, and since VERA-260 that is a
 * named half rather than the whole question — see section 6, `AnalyticsMapGrouping`. "One
 * pin per located event" is one granularity the map can draw; "one filled polygon per
 * country" is another, sourced from `getCountryMetrics` rather than `getScanMapPoints`,
 * and it can place things a pin cannot (a RATE has no coordinate but colours an area
 * perfectly well). `COUNTRY_GROUPABLE` is this record's counterpart on that axis. Do not
 * widen this one to admit a metric that is only country-groupable: it would claim the pin
 * map can draw something it cannot, which is the exact failure this file's header names.
 */
const MAP_RENDERABLE: Record<AnalyticsSeriesDimension, boolean> = {
  scans: true,
  certificates: true,
  /*
    VERA-260 — the third mappable dimension, and the only one this change adds.

    An invalid scan is a row in the `scans` table with `invalid = true`; it carries
    `geo_lat`/`geo_lng` like any other, and `getScanMapPoints` already accepts
    `scan_statuses` and applies it to the very rows it plots. So no server work was needed
    — the affordance was withheld only because this record could not express the answer.

    ⚠️ THE MAP DRAWS A SUBSET OF THE CARD'S FIGURE, in two ways, both pre-existing and
    neither introduced here: a scan whose visitor declined a location is not plotted (true
    of every dimension on this map), and the map's `JOIN certificates` drops an invalid
    scan that matched NO certificate — the population `mkt_invalid_scans` already carries
    its own "further invalid scans matched no certificate at all" note for. A pin count
    below the card's number is therefore expected, and both causes are already stated on
    the surfaces that own them.
  */
  invalid_scans: true,
  transfers: false,
  stolen_reports: false,
  lost_reports: false,
  resales: false,
}

/**
 * What the map card's `dataType` accepts. The WIDE union — see `MAP_RENDERABLE`.
 *
 * An alias rather than a fourth union, so it cannot drift from the record that decides
 * which of its members are actually capable. Named for the surface because that is what a
 * reader of `ScansByLocation`'s props is asking about.
 */
export type AnalyticsMapDimension = AnalyticsSeriesDimension

/**
 * ── THE SCAN OUTCOME A MAP DIMENSION PINS, IF ANY (VERA-260) ────────────────────────
 *
 * `MAP_RENDERABLE`'s docblock claims `invalid_scans` is drawn by `scan_statuses:
 * ['invalid']`. This is that claim in executable form, living beside it so the two cannot
 * disagree — a prose table naming a request that no code issues is how a capability record
 * comes to promise something the renderer does not do.
 *
 * `null` means "this dimension does not narrow by outcome": `scans` draws every outcome
 * and `certificates` reaches its one-point-per-certificate answer through `mode: 'latest'`
 * instead. Neither is the same as "no pin decided yet", which is why the record is TOTAL
 * over the union — a dimension added to the statistics response is a compile error here
 * until someone says which outcome, if any, it selects.
 *
 * ⚠️ THE PIN IS THE CARD'S IDENTITY AND MUST SURVIVE THE PAGE FILTER. A card holding a pin
 * INTERSECTS the page's `scanStatuses` into it rather than letting either win outright —
 * see `withAnalyticsScanStatuses`, and `ScansByLocation` for why an empty intersection must
 * suppress the request rather than send `scan_statuses: []` (which the server reads as no
 * narrowing at all, i.e. every scan).
 */
const MAP_DIMENSION_SCAN_STATUSES: Record<
  AnalyticsMapDimension,
  readonly string[] | null
> = {
  scans: null,
  certificates: null,
  invalid_scans: ['invalid'],
  transfers: null,
  stolen_reports: null,
  lost_reports: null,
  resales: null,
}

/** The outcomes this map dimension is pinned to, or `null` when it pins none. */
export const mapDimensionScanStatuses = (
  dimension: AnalyticsMapDimension,
): readonly string[] | null => MAP_DIMENSION_SCAN_STATUSES[dimension]

/**
 * ⚠️ WHETHER THE EVENTS CARRY A LOCATION AT ALL — a DIFFERENT question from whether the
 * map can draw them, and conflating the two would make this file assert a falsehood.
 *
 * Before VERA-260 the map had one failure branch and one sentence: "X are not stored with
 * a location". That was true of every dimension that reached it, because only two
 * dimensions reached it. It stopped being true the moment `invalid_scans` became a
 * dimension: an invalid scan is a row in the `scans` table like any other and it carries
 * `geo_lat`/`geo_lng` — 245 of the 248 in the dev database do. Telling a reader those
 * events have no location would be exactly the plausible-looking falsehood this file's
 * header exists to refuse.
 *
 * So the map now answers the same two-part question the chart does: is the location
 * RECORDED (here), and can the renderer PLOT it (`MAP_RENDERABLE`). `invalid_scans` was
 * located and not yet plottable when this note was written; that gap is now closed there.
 *
 * A transfer, a resale and a loss report are records of an OWNERSHIP event, not of a
 * device touching a tag — nothing in the transfer or certificate row holds coordinates.
 * That is a property of the data model rather than a missing column.
 *
 * ## ⚠️ "GEO VIA THE CERTIFICATE'S SCANS" WAS PROPOSED FOR THE LOSS REPORTS AND REJECTED
 *
 * The proposal was to let `stolen_reports` / `lost_reports` onto the map by locating each
 * report through the scans of the certificate it concerns. It is the `scanGeoExists`
 * approximation — "every scan of a certificate that has at least one matching row" — which
 * `analytics.postgres.repository.ts` already refuses for scan OUTCOME, and it fails here
 * for a sharper reason than it does there: the cardinality is not off at the margin, it is
 * off by two orders of magnitude.
 *
 * Measured on the dev database (Louis Vuitton, all time): 3 `stole` and 2 `loose` rows in
 * `certificate_action_logs`, against 214 located scans on the one certificate currently in
 * `stolen` and 236 on the one in `lost`. A card titled "Reported stolen" reading 3 would
 * have drawn 214 pins. Over the DEFAULT range (the billing period) it is worse in the
 * other direction — 0 report events, so the card reads 0 while the certificate's scans are
 * still there to plot.
 *
 * The two are not the same measurement either, and the ROI card's own description says so:
 * it counts the DATED REPORT EVENT, whereas `certificate_statuses` — the only predicate the
 * map endpoint has — selects on the certificate's CURRENT status. A certificate reported
 * stolen and since recovered is in the count and not in the filter.
 *
 * `certificate_action_logs` carries `id, type, certificate_id, snapshot, metadata,
 * user_type, user_id, created_at, company_id` — no coordinates, and no row-level link to
 * the scan that prompted the report. Locating a report honestly would mean a new endpoint
 * mode returning one point per REPORT (located via that certificate's nearest scan in
 * time), which is real server work and a real product decision about what that pin means.
 * Until that exists, `false` is the true answer rather than the unfinished one.
 */
const MAP_LOCATED: Record<AnalyticsSeriesDimension, boolean> = {
  scans: true,
  certificates: true,
  invalid_scans: true,
  transfers: false,
  stolen_reports: false,
  lost_reports: false,
  resales: false,
}

/* ────────────────────────────────────────────────────────────────────────────────────
   2b. CAN THIS DIMENSION BE BROKEN DOWN INTO RANKED ROWS? (VERA-260)
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * ── THE TABLE'S QUESTION IS NOT THE CHART'S OR THE MAP'S ───────────────────────────
 *
 * Chart asks "is there a dated history?"; Map asks "do the events carry a location?".
 * Table asks a third thing: **is there anything to put in the ROWS?** A scalar has no
 * rows. Only a metric with a BREAKDOWN DIMENSION — an axis its events can be grouped
 * and ranked on — has a table.
 *
 * That distinction is the reason the table exists at all. Several figures a brand wants
 * are DISTRIBUTIONS wearing a scalar's clothes: "average resales per product" is the
 * same number whether one product resold forty times or forty resold once, and the
 * average is precisely the statistic that destroys the difference. A ranked breakdown is
 * where that shape lives.
 *
 * ## The property it rests on: the leaderboard endpoint's own grouping axis
 *
 * `analytics/admin/top-influencers` groups the `scans` table by owner / product /
 * country / city. Everything it can rank, it ranks from a `scans` row joined to its
 * certificate and product — so the dimension it can break down is `scans`, and only
 * `scans`.
 *
 * ⚠️ `certificates` is FALSE, and the reason is the honest one rather than "not done
 * yet". "Owners ranked by certificates HELD" is a real and useful question, but it is a
 * different query over a different table (`certificates` grouped directly, with no scan
 * involved) and a different set of columns — `leads` and `last_scan_at` are meaningless
 * for it. Offering the affordance now would produce a card whose columns are named for
 * scan activity and whose ranking is not about scans. Better a stated absence than a
 * table that quietly answers a question nobody asked.
 *
 * ⚠️ KEYED ON THE WIDE UNION, and it was narrow (`AnalyticsDataType`) until VERA-260 —
 * the same mistake, with the same cause, that `MAP_RENDERABLE`'s docblock above records
 * for itself. A two-member key made the question UNASKABLE for the other five dimensions:
 * `invalid_scans` fell through to "not broken down by owner, product or place" with no
 * entry anywhere saying whether that was a decision or an omission. Now every dimension
 * has to be answered for, and the answers below are answers rather than defaults.
 *
 * `AnalyticsDataType` is NOT widened to reach this, for the reason `MAP_RENDERABLE` gives:
 * it is also `LEGACY_PANEL_METRIC_ID`'s key, and a dimension with no legacy panel has no
 * answer to give there.
 *
 * TOTAL, so a `*_count` added to the statistics bucket row is a compile error here until
 * someone says whether it can be ranked — the discharge-by-compiler the file header
 * describes, now applied on the axis that actually varies.
 */
const TABLE_RANKABLE: Record<AnalyticsSeriesDimension, boolean> = {
  scans: true,
  certificates: false,
  /*
    FALSE, and not for want of a query. The leaderboard CAN restrict to invalid scans —
    `scan_statuses: ['invalid']` is the same request `MAP_RENDERABLE` uses to make
    `invalid_scans` mappable, and the endpoint honours it. What it cannot do is SAY so:
    every numeric column on the card is titled for the unrestricted population ("Scans",
    "Leads", "Certificates"), so a card headed "Invalid scans by owner" would rank on a
    column labelled Scans whose figure is invalid scans. That is the same
    plausible-looking falsehood this file exists to refuse, and closing it means
    dimension-aware column headers, not a `true` here.
  */
  invalid_scans: false,
  // The endpoint groups `scans` rows. A transfer, a report and a resale are rows in other
  // tables with no scan involved, so there is nothing for this query to rank — the same
  // structural absence `MAP_LOCATED` records for them, one table over.
  transfers: false,
  stolen_reports: false,
  lost_reports: false,
  resales: false,
}

/**
 * ── WHAT "TOP" MEANS, PER CARD — orthogonal to what a row IS (VERA-260) ─────────────
 *
 * The leaderboard's `dimension` decides what a row is (an owner, a product, a place) and
 * its `rank_by` decides which column the rows are ordered and TRUNCATED on. Two axes, and
 * conflating them is what kept "leads by owner" unreachable: the card had a `Leads` column
 * from the day it shipped, and the ranking behind it was always scans, so the owners who
 * brought the most new scanners were only visible if they also happened to be among the
 * ten most-scanned.
 *
 * ⚠️ THIS IS WHY A CARD CAN BE TABLE-CAPABLE WITH NO SERIES DIMENSION. `mkt_leads_captured`
 * is deliberately absent from `ROI_CARD_DIMENSION` — a lead is a DISTINCT count, so it
 * cannot be bucketed into a chart (one email scanning on Monday and Tuesday is one lead in
 * the card and two in a per-bucket count, and a series that does not sum to the figure
 * beside it is worse than no series). None of that bears on a RANKING: `COUNT(DISTINCT
 * lead_email)` per owner is exact, and the leaderboard has computed it all along.
 *
 * So the table's capability is answered by EITHER path — an entry here, or a rankable
 * series dimension — and the chart's and the map's are unaffected. Mapping leads to the
 * `scans` dimension to reach the table would have been the tempting shortcut and would
 * have handed the chart a plottable series it must not have, titled "Leads captured" and
 * showing scans.
 *
 * Authored, and short on purpose. Same standard as `ROI_CARD_DIMENSION`: an entry asserts
 * that the leaderboard's column and the card's figure are the same MEASUREMENT. `leads`
 * here and `mkt_leads_captured` both count distinct non-owner scanner emails over the
 * window, from `scans`, under the same filters — see `notAnOwnerScanFilter` and its raw-SQL
 * twin, which a parity guard keeps identical.
 */
const ROI_CARD_RANK_KEY: Record<string, TopInfluencerRankKey> = {
  mkt_leads_captured: 'leads',
}

/**
 * Which column this card's leaderboard ranks on. `scans` unless a card says otherwise —
 * the server's own default, restated here so the request and the caption agree.
 */
export const analyticsCardRankKey = (cardId: string): TopInfluencerRankKey =>
  ROI_CARD_RANK_KEY[cardId] ?? 'scans'

/* ────────────────────────────────────────────────────────────────────────────────────
   3. WHICH DIMENSION DOES A CARD COUNT?
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * The KPI id space, asserted to be totals fields.
 *
 * A KPI tile's id IS the totals field it displays — stated in `OVERVIEW_STAT_BOXES` and
 * in `ANALYTICS_TILE_IDS`, and it is what makes the lookup below a derivation rather than
 * a mapping table. The annotation is the enforcement: a tile id that stops being a
 * `keyof GetAnalyticsTotalsCounts` is a compile error here.
 */
const TILE_TOTALS_FIELDS: readonly (keyof GetAnalyticsTotalsCounts)[] =
  Object.values(ANALYTICS_TILE_IDS)

const TILE_FIELD_SET = new Set<string>(TILE_TOTALS_FIELDS)

/**
 * ── THE AUTHORED HALF: ROI CARD -> THE DIMENSION IT COUNTS (VERA-260) ────────────────
 *
 * A KPI tile needs no entry here — its id IS its totals field, so the lookup below
 * derives it. An ROI card's id is its own thing (`log_resales`), and the codebase already
 * ruled, at length and twice, that bridging that gap by matching on TITLE is forbidden:
 * "Counterfeit Deflections" is a COUNT of incidents while the registry's
 * `counterfeit_deflection_value` is a CURRENCY metric feeding the ROI hero, so a fuzzy
 * match would make a count card claim to feed the hero.
 *
 * The sanctioned fix was always "an AUTHORED link, decided by someone who can say the two
 * are the same MEASUREMENT". This is that link, and it is deliberately four entries long
 * rather than a rule. Each was checked against the SQL that produces both sides:
 *
 * | card | series field | same measurement because |
 * |---|---|---|
 * | `mkt_invalid_scans` | `invalid_scans_count` | both count `scans` rows with `invalid = true`, over the same range |
 * | `legal_stolen_reports` | `stolen_reports_count` | both count the dated stolen-report event, not the current state |
 * | `legal_lost_reports` | `lost_reports_count` | both count the dated lost-report event, not the current state |
 * | `log_resales` | `resales_count` | both count transfers completed AS A SALE, gifts and first handoff excluded |
 *
 * Verified numerically when the series were added, not merely reasoned about: each
 * series summed over an all-time range equals its card (248, 3, 2, 22).
 *
 * ⚠️ WHAT IS DELIBERATELY ABSENT IS THE INTERESTING PART. Every other ROI card stays
 * `null`, and for three distinct reasons, none of which is "not done yet":
 *
 *   - **`DISTINCT`-based counts** (`mkt_leads_captured`, `mkt_geo_reach`) cannot be
 *     bucketed. One email scanning on Monday and Tuesday is ONE lead in the card and TWO
 *     in a naive per-bucket count, so the series would not sum to the figure beside it —
 *     the one invariant that makes a series trustworthy.
 *   - **Rates, means and medians** (`mkt_invalid_scan_rate`, `log_avg_resale_price`,
 *     `mkt_time_to_first_scan`, `log_items_per_owner`, `mkt_repeat_scan_rate`) are not
 *     counts. Plotting them needs a value-per-bucket path, which is a different shape
 *     from a count-per-bucket one, and inventing one here would silently redefine the
 *     statistic.
 *   - **Registry/ROI currency metrics** feed the hero and are computed for a window
 *     rather than accumulated over it.
 *
 * Each of those comes back with the honest sentence `analyticsDisplayCapability` builds
 * below, which is the outcome this file's header calls "the feature working".
 */
const ROI_CARD_DIMENSION: Record<string, AnalyticsSeriesDimension> = {
  mkt_invalid_scans: 'invalid_scans',
  legal_stolen_reports: 'stolen_reports',
  legal_lost_reports: 'lost_reports',
  log_resales: 'resales',
}

/**
 * The dimension a card counts, or `null` when it counts nothing this page can address.
 *
 * Two id spaces, two mechanisms, in that order: a KPI tile DERIVES its dimension from its
 * id (the id is the totals field), and an ROI card looks its up in the authored table
 * above. The derivation is checked first because it is the one that cannot go stale.
 */
export const analyticsCardDimension = (cardId: string): string | null =>
  TILE_FIELD_SET.has(cardId) ? stripCount(cardId) : (ROI_CARD_DIMENSION[cardId] ?? null)

/** `completed_transfers` -> `Completed transfers`. For the reason sentences. */
const dimensionLabel = (dimension: string): string => {
  const words = dimension.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * Is this dimension one the CHART is typed to accept? Narrows to the wide union.
 *
 * Keyed on `CHART_RENDERABLE` rather than on `SERIES_DIMENSIONS`: both are total over the
 * same type and therefore hold the same members, but only the record carries the type, so
 * only it can narrow.
 */
const isSeriesDimension = (dimension: string): dimension is AnalyticsSeriesDimension =>
  Object.prototype.hasOwnProperty.call(CHART_RENDERABLE, dimension)

/*
  There was a second narrowing helper here — `isDataTypeDimension`, narrowing to
  `AnalyticsDataType` because `TABLE_RANKABLE` was keyed on it. It is gone with that key
  (VERA-260): all three records are now total over `AnalyticsSeriesDimension`, so
  `isSeriesDimension` above narrows for every one of them and a second helper could only
  ever drift from it. The map lost its own for the same reason a release earlier.

  The disagreement the old helper existed to preserve — the map accepting `invalid_scans`
  while the leaderboard does not — did not depend on the KEY and has not been lost. It is
  stated where it belongs, as `invalid_scans: false` in `TABLE_RANKABLE` beside the reason,
  rather than as an absence from a union.
*/

/* ────────────────────────────────────────────────────────────────────────────────────
   4. THE ANSWER
   ──────────────────────────────────────────────────────────────────────────────────── */

const CAPABLE: DisplayCapability = { capable: true }

/**
 * A card that counts nothing addressable — every ROI card, and anything whose id is not
 * a totals field. One figure, computed for a window; there is no per-event row behind it
 * to bucket or to place.
 */
const NO_DIMENSION_REASON: Record<Exclude<AnalyticsDisplay, 'metric'>, string> = {
  chart:
    'Vera records this as a single figure for the selected period, not as a dated history, so there is nothing to plot over time.',
  /*
    ⚠️ IT USED TO SAY "the events behind it are not stored with a location", AND FOR SOME
    OF THESE CARDS THAT WAS FALSE (VERA-260).

    This one sentence is served to EVERY card with no addressable dimension, and three of
    them are about geography: `mkt_geo_reach` (Countries reached), `mkt_invalid_scan_rate`
    and `mkt_repeat_scan_rate` are all derived from `scans` rows, which carry
    `geo_lat`/`geo_lng` — `mkt_geo_reach` is literally counted from the located ones. So
    the page was telling a reader who asked why Countries reached has no map that the scans
    behind it have no location, one card away from a map plotting those very scans. A
    plausible-looking falsehood is the failure mode this file's header exists to refuse, and
    it does not stop being one because it happens to be the default branch.

    The replacement asserts only what is actually derivable here: there is no per-event
    dimension the page can address, so there is nothing to place. That is true of a rate, of
    a distinct count, and of an ROI currency figure alike, and it does not make a claim
    about the underlying rows that this code has not checked.

    ## The specific answer for the geographic three, recorded rather than rendered

    They are not "unmappable" so much as WRONGLY SHAPED FOR THIS MAP. `mkt_geo_reach` is a
    DISTINCT COUNT over countries and the other two are RATES; this card plots one pin per
    event. Pins cannot express a ratio — a pin is present or absent, so a 4% invalid rate
    and a 96% one draw the identical picture — and a pin map of Countries reached would
    simply be the scans map with a different title, i.e. two cards showing one measurement
    under two names, which is the duplicate the `mkt_consumer_touchpoints` removal already
    ruled out.

    The right render for all three is a per-country CHOROPLETH, and one is BEING BUILT in
    parallel — `d887f04ef` landed the server half (per-country aggregates plus a
    name->ISO join that reports its misses) and `packages/ui`'s `choropleth.ts` /
    `MapChoroplethLegend` are the client half.

    ⚠️ THAT DOES NOT MAKE THESE THREE `MAP_RENDERABLE`, and the distinction matters when the
    choropleth lands. `MAP_RENDERABLE` answers for THIS card — a pin per located event —
    and a rate has no located event to pin. A choropleth is a different render of a
    different aggregate, so it earns its own display value on the category axis (and its own
    capability record keyed on whatever dimension set the country endpoint can group), not a
    `true` here. Widening this record to admit them would be exactly the "affordance offered
    where the data cannot honour it" the file header names.

    Deliberately NOT expressed as a per-card reason table: a hardcoded list of ids is the
    flag this file's header forbids, and it would go stale in the direction that lies. The
    honest answer is in the sentence below and the specifics are here, where the next person
    to ask reads.
  */
  map: 'Vera records this as a single figure for the selected period. There is no per-event breakdown behind it for this page to place on a map.',
  // Says "nothing to rank", not "nothing to plot" — the table's absence has a different
  // cause from the chart's and a reader comparing two greyed chips should be able to
  // tell them apart. A single figure has no rows because it has no BREAKDOWN, which is
  // true even for a metric that does have a dated history.
  table:
    'Vera records this as a single figure for the selected period. There is no breakdown behind it to rank, so a table would have one row.',
}

/**
 * ── CAN THIS CARD BE DRAWN AS A PIN MAP? ────────────────────────────────────────────
 *
 * One mark per located EVENT. This is the answer `analyticsDisplayCapability` used to
 * compute inline as its `map` column, EXTRACTED VERBATIM (VERA-260) — every sentence
 * below is the one that already shipped, moved rather than rewritten.
 *
 * ⚠️ IT HAD TO COME OUT, because `.map` no longer means this. Grouping is an axis now
 * (`AnalyticsMapGrouping`), so "can this be a map" is the UNION over groupings while "can
 * this be a PIN map" is one column of it, and the two genuinely disagree: an invalid scan
 * RATE has no event to pin and is a canonical choropleth. Leaving `.map` as the pin answer
 * would have left every rate's `Display as -> Map` chip disabled, one grouping away from a
 * map that has been able to draw it since the country endpoint shipped — the same
 * "plausible-looking falsehood on the default branch" `NO_DIMENSION_REASON.map` above
 * records itself having shipped.
 *
 * The delegation direction is therefore INVERTED from the groundwork commit's:
 * `analyticsMapGroupingCapability(id,'pin')` now delegates HERE, and `.map` is composed
 * from both columns. Still exactly one expression of the pin rule; it just moved down a
 * level so the union could be built on top of it.
 */
export const analyticsMapPinCapability = (cardId: string): DisplayCapability => {
  const dimension = analyticsCardDimension(cardId)
  const valueDimension = analyticsCardValueDimension(cardId)

  /*
    The VALUE path. A rate/mean/median has no count dimension; a pin is present or absent,
    so 4% and 96% draw the identical picture. (The right render IS the choropleth — which
    is why this sentence is no longer the last word on the subject, and why the country
    column exists.)
  */
  if (!dimension && valueDimension) {
    return {
      capable: false,
      reason: `${analyticsValueLabel(valueDimension)} is a rate over events rather than a count of them, and a pin can only show that an event happened. There is nothing to place on a map.`,
    }
  }
  if (!dimension) return { capable: false, reason: NO_DIMENSION_REASON.map }

  const label = dimensionLabel(dimension)
  const seriesDimension = isSeriesDimension(dimension)

  /*
    Two failure branches, matching the chart's shape — see `MAP_LOCATED` for why one
    sentence could no longer cover both. The order matters: "no location" is the stronger,
    permanent fact and is checked first, so a dimension that is neither located nor
    plottable is described by the reason a reader can act on (they cannot).
  */
  if (!(seriesDimension && MAP_LOCATED[dimension])) {
    return {
      capable: false,
      reason: `${label} are not stored with a location, so there is nothing to place on a map.`,
    }
  }
  if (!MAP_RENDERABLE[dimension]) {
    return {
      capable: false,
      reason: `${label} are stored with a location, but this map cannot yet plot them.`,
    }
  }
  return CAPABLE
}

/**
 * Which displays this card supports, and why not where it does not.
 *
 * `metric` is unconditionally capable: every card in scope here IS a metric, so the
 * question is only ever whether it can ALSO be drawn some other way.
 *
 * ⚠️ `map` IS THE UNION OVER GROUPINGS (VERA-260) — capable when the card can be drawn as
 * a pin map OR as a choropleth. See `analyticsMapCapability`, and `analyticsMapPinCapability`
 * for the column that used to be computed here.
 */
export const analyticsDisplayCapability = (
  cardId: string,
): Record<AnalyticsDisplay, DisplayCapability> => {
  const dimension = analyticsCardDimension(cardId)

  /*
    A card with a RANK KEY but no series dimension is table-capable and nothing else —
    `mkt_leads_captured` is the case, and `ROI_CARD_RANK_KEY`'s docblock says why the two
    axes come apart. Checked BEFORE the no-dimension short-circuit below, or the early
    return would answer "no breakdown to rank" for a card whose whole point is that it has
    one.

    Chart and map keep the no-dimension sentences unchanged, which is the honest pair: a
    distinct count genuinely has no series to plot and no per-event row to place.
  */
  const rankKeyOnly = !dimension && cardId in ROI_CARD_RANK_KEY

  /*
    ── THE VALUE PATH, CHECKED BEFORE THE NO-DIMENSION SHORT-CIRCUIT (VERA-260) ────────

    A rate/mean/median card has no COUNT dimension, so it used to fall straight through to
    the branch below and be told "Vera records this as a single figure for the selected
    period, not as a dated history" — which is now FALSE for these six, and false in the
    specific way this file's header is about: a plausible sentence, on the default branch,
    asserting an absence that the sink no longer has.

    Chart only. The map and the table stay incapable and keep their existing sentences,
    which remain true and are the honest answers rather than placeholders:
      - a MAP plots one pin per located event, and a rate has no event to pin. A pin is
        present or absent, so a 4% invalid rate and a 96% one draw the identical picture.
        (The right render for a rate over geography is the per-country choropleth, which is
        its own display value on the category axis — see `NO_DIMENSION_REASON.map`.)
      - a TABLE needs ranked rows, and the leaderboard groups `scans` rows. None of these
        six is a scan count, so there is nothing for it to rank.
  */
  const valueDimension = analyticsCardValueDimension(cardId)
  if (!dimension && valueDimension) {
    return {
      metric: CAPABLE,
      chart: CAPABLE,
      // ⚠️ NO LONGER AN UNCONDITIONAL REFUSAL (VERA-260). Two of the six value dimensions
      // — the invalid-scan rate and the repeat-scan rate — are `COUNTRY_GROUPABLE`, and a
      // choropleth is the canonical render for a rate over geography. The union answers
      // for all six; the pin half's sentence (which is still the honest one for the other
      // four) lives in `analyticsMapPinCapability`.
      map: analyticsMapCapability(cardId),
      table: rankKeyOnly
        ? CAPABLE
        : { capable: false, reason: NO_DIMENSION_REASON.table },
    }
  }

  if (!dimension) {
    return {
      metric: CAPABLE,
      chart: { capable: false, reason: NO_DIMENSION_REASON.chart },
      map: analyticsMapCapability(cardId),
      table: rankKeyOnly
        ? CAPABLE
        : { capable: false, reason: NO_DIMENSION_REASON.table },
    }
  }

  const label = dimensionLabel(dimension)
  const seriesDimension = isSeriesDimension(dimension)

  const chart: DisplayCapability = !SERIES_DIMENSIONS.has(dimension)
    ? {
        capable: false,
        // The precise failure: the count exists, the HISTORY does not. This is the
        // `VRG-362` shape — current state stored, events not — and naming it that way is
        // what stops a reader reading the empty affordance as a bug.
        reason: `Vera records the current number of ${label.toLowerCase()}, not a dated history of them, so there is nothing to plot over time.`,
      }
    : !(seriesDimension && CHART_RENDERABLE[dimension])
      ? {
          capable: false,
          // The other failure: the series exists and no accessor reads it. Said plainly
          // rather than dressed as a data gap, because it is ours to close.
          reason: `${label} are recorded over time, but this chart cannot yet plot them.`,
        }
      : CAPABLE

  // The union over groupings. Its pin column is the two-branch check that used to be
  // spelled out here — moved to `analyticsMapPinCapability`, verbatim, so the pin rule
  // still has exactly one expression.
  const map: DisplayCapability = analyticsMapCapability(cardId)

  /*
    The table needs only ONE check, unlike the chart's conjunction of sink-and-renderer.
    `TABLE_RANKABLE` already IS the conjunction: the leaderboard endpoint and the card
    that draws it were built together in one change, so there is no gap between "the
    server can group this" and "the client can render that grouping" for the code to
    disagree about. The moment those two can diverge — a dimension the endpoint ranks
    that the card has no columns for — this gets a second Record and starts reading like
    `chart` above.
  */
  const table: DisplayCapability =
    // A card can reach the table by EITHER route: its own rank key, or a rankable series
    // dimension. See `ROI_CARD_RANK_KEY` for why the two are separate axes.
    cardId in ROI_CARD_RANK_KEY || (seriesDimension && TABLE_RANKABLE[dimension])
      ? CAPABLE
      : {
          capable: false,
          // Names what a table WOULD need, not merely that it is unavailable. §46's
          // forcing function: a reason that cannot say what is missing is a dead
          // control wearing an explanation.
          reason: `${label} are not broken down by owner, product or place, so there are no ranked rows to show.`,
        }

  return { metric: CAPABLE, chart, map, table }
}

/* ────────────────────────────────────────────────────────────────────────────────────
   5. THE FORWARD MAP — legacy card ids, read-time only
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * ── VRG-481: THE LEGACY PANELS ARE NOW `(metric, display)` PAIRS ────────────────────
 *
 * `scans_by_time` and `scans_by_location` were standalone cards with a Scans/Certificates
 * toggle in their headers. They are gone: `Total Scans` with Chart on IS the first, and
 * `Total Certificates` with Chart on is what the toggle used to reach. One library row per
 * metric, display as a property of it.
 *
 * Their ids, however, are PERSISTED — in `analyticsViewSeeds` (ours, rewritten) and in
 * every user's saved views under preferences (not ours, and not rewritable). So they have
 * to keep resolving.
 *
 * ## Applied on READ, never as a rewrite of stored preferences
 *
 * A read-time map is reversible: it changes nothing on disk, so a view it maps wrongly can
 * be fixed by changing this function, and a view it does not understand is left exactly as
 * the user saved it. A bulk rewrite is neither — it is a one-way transformation of data we
 * do not own, run once, with no way to tell afterwards what the original said.
 *
 * ## The DIMENSION comes from the view's own filters, and that is the whole subtlety
 *
 * A legacy panel's dimension lived in `filters.chartDataType` / `filters.mapDataType`
 * rather than in its id, so `scans_by_time` in a view holding `chartDataType:
 * 'certificates'` was showing CERTIFICATES over time. Mapping it to `scans_count::chart`
 * unconditionally would silently repoint that card at a different measurement — a view
 * that opens looking right and is showing the wrong series, which is worse than a card
 * that fails to open.
 *
 * Reading the pair together is what makes AC4 ("still opens, showing the same cards") true
 * rather than approximately true.
 *
 * ⚠️ An id with no rule here is returned UNCHANGED, not dropped. Dropping is
 * `intersectOrdered`'s job — it already filters the result against `availableCardIds`, so
 * an id that resolves to nothing falls out there, through the one path that has always
 * handled this. A second dropping mechanism here would mean two places to look when a card
 * goes missing from a view.
 */
export const LEGACY_ANALYTICS_PANEL_IDS = {
  scansByTime: 'scans_by_time',
  scansByLocation: 'scans_by_location',
} as const

/**
 * The metric card a legacy panel's dimension resolves to.
 *
 * ⚠️ TOTAL over `AnalyticsDataType`, so a third dimension added to that union is a compile
 * error here until someone says which metric card it maps to — rather than a legacy view
 * that silently loses a card. The values are the KPI tile ids, referenced rather than
 * spelled, so a tile rename moves this with it.
 */
const LEGACY_PANEL_METRIC_ID: Record<AnalyticsDataType, string> = {
  scans: ANALYTICS_TILE_IDS.scans,
  certificates: ANALYTICS_TILE_IDS.certificates,
}

/** The dimension keys a stored view can carry. `dataType` is the pre-split page-level key. */
export interface LegacyViewDimensions {
  chartDataType?: AnalyticsDataType
  mapDataType?: AnalyticsDataType
  dataType?: AnalyticsDataType
}

/**
 * One id, forward-mapped. Returns the id unchanged when no rule applies — see above.
 *
 * The `?? dataType ?? 'scans'` chain is the same reading `applyAnalyticsState` already
 * uses: before the per-panel split there was ONE `dataType` driving both, so falling back
 * to it is the faithful interpretation of an older view rather than a guess.
 */
export const migrateAnalyticsCardId = (
  cardId: string,
  dimensions: LegacyViewDimensions | undefined,
): string => {
  const d = dimensions ?? {}
  if (cardId === LEGACY_ANALYTICS_PANEL_IDS.scansByTime) {
    return analyticsVariantCardId(
      LEGACY_PANEL_METRIC_ID[d.chartDataType ?? d.dataType ?? 'scans'],
      'chart',
    )
  }
  if (cardId === LEGACY_ANALYTICS_PANEL_IDS.scansByLocation) {
    return analyticsVariantCardId(
      LEGACY_PANEL_METRIC_ID[d.mapDataType ?? d.dataType ?? 'scans'],
      'map',
    )
  }
  /*
    ── `scans_by_country` IS A GROUPING NOW, NOT A CARD TYPE (VERA-260) ───────────────

    The choropleth shipped as its own standalone card because grouping was not yet an
    axis — a build-order artefact `AnalyticsMapGrouping`'s docblock names as such. It is
    the scans map grouped by country, so it forward-maps to exactly that id and the
    standalone card is retired.

    ⚠️ THE ALIAS IS NOT OPTIONAL, and it is the reason this is a `migrateAnalyticsCardId`
    entry rather than a deletion. `scans_by_country` is PERSISTED — in saved views, in
    every layout item's `i`, and in the `lastViewState` of anyone who had the card on
    their dashboard. Without a rule here it becomes an id `availableCardIds` no longer
    contains, and `intersectOrdered` drops it silently: a reader's saved dashboard would
    open one card short, with nothing saying why.

    ## Why `scans` specifically, out of the card's three metrics

    The standalone card carried a metric PICKER (`scans` / `invalid_rate` / `repeat_rate`)
    whose choice was never persisted — it was `useState` inside the card, so every reload
    already opened it on its `scans` default. Mapping to the scans choropleth is therefore
    the faithful reading of a stored view, not a lossy one: it restores precisely the
    state that view actually reopened in. The other two are reachable as their own cards
    (`mkt_invalid_scan_rate::map@country`, `mkt_repeat_scan_rate::map@country`) — the
    picker's three columns map onto three `COUNTRY_GROUPABLE` entries exactly, which is
    what makes retiring the picker a decomposition rather than a capability loss.
  */
  if (cardId === SCANS_BY_COUNTRY_CARD_ID) {
    return analyticsVariantCardId(ANALYTICS_TILE_IDS.scans, 'map', 'country')
  }
  return cardId
}

/**
 * A whole ordered card list, forward-mapped and DEDUPED.
 *
 * Dedupe is not tidiness. A view that holds BOTH `scans_by_time` and the variant it maps
 * to — entirely possible, since both were offered in the library at once — would otherwise
 * come back with the same id twice, and react-grid-layout keys its children by id: two
 * children with one key is a React duplicate-key warning and a grid that drops one of them
 * unpredictably. First occurrence wins, so the view's own order is preserved.
 */
export const migrateAnalyticsCardIds = (
  cardIds: readonly string[],
  dimensions: LegacyViewDimensions | undefined,
): string[] => {
  const seen = new Set<string>()
  const out: string[] = []
  for (const id of cardIds) {
    const next = migrateAnalyticsCardId(id, dimensions)
    if (seen.has(next)) continue
    seen.add(next)
    out.push(next)
  }
  return out
}

/* ────────────────────────────────────────────────────────────────────────────────────
   BUCKET GRANULARITY — how finely the time chart asks the API to slice a range
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * Bucket by HOUR at or below this span, by DAY above it.
 *
 * 48h and not 24h: a two-day window is 48 hourly buckets, which is still a readable
 * density, and a one-day cliff would make a range of "yesterday 9am to today 9am" fall to
 * daily and collapse to two points. Above 48h the bucket count grows past what the axis
 * can label — a 30-day range is 720 hours — so the day bucket is right there, not merely
 * cheaper.
 *
 * ⚠️ MOVED HERE FROM `ScansByTime2` (VERA-260), where it was a module-private constant.
 * The CSV export has to send the SAME `interval_granularity` the chart sent, or a file
 * headed "Total Scans" holds daily buckets under a chart drawn in hours — the rows would
 * still sum to the same total, so nothing would look wrong, and the reader would simply
 * have a different measurement from the one on screen. Two copies of a 48-hour threshold
 * is exactly the drift §44 is about, so there is one, and it lives in a module the chart
 * and the export can both reach (`ScansByTime2` is a page component and cannot be
 * imported from here).
 */
export const ANALYTICS_HOURLY_MAX_SPAN_MS = 2 * 24 * 60 * 60 * 1000

/**
 * The bucket width a range asks for, as the two request parameters that express it.
 *
 * ⚠️ RETURNS BOTH HALVES, because the API's bucket is `'<count> <granularity>'` — the
 * `interval` string `getGranularStats` builds and interpolates into BOTH the
 * `generate_series` spine and every row-attach predicate. A week is therefore `7 day`,
 * not a `Granularity` member: the server enum is `hour | day | month` and adding a
 * `week` to it would mean a new enum member, a new DTO value and new SQL to express
 * something `interval_count` already says. Spread this into the request params.
 */
export interface AnalyticsChartBucket {
  interval_granularity: 'hour' | 'day' | 'month'
  interval_count: number
}

/**
 * The bucket width a range asks for — hour, day, week or month, stepped by range LENGTH.
 *
 * ⚠️ IT STEPS PAST `day` SINCE VRG-512, and the missing steps are why a 12-month range
 * used to draw ~365 points. A daily bucket over a year is more marks than the axis can
 * label and more than the eye can resolve, so the line reads as noise regardless of how
 * clean the underlying data is — the reader sees one pixel-wide spike per day and calls
 * the chart jagged. That is a RENDER defect, not a data defect, and it is the half of
 * "the scans chart looks jagged" that no amount of seed tuning could fix.
 *
 * ── THE LADDER IS TWO STEPS: DAY UP TO ~3 MONTHS, MONTH ABOVE IT ─────────────────────
 *
 * Product directive, and it replaces a delegation to `roiValueGranularity` (<=45d day,
 * <=182d week, else month). The rule now is the one a reader can state: a quarter or less
 * is read day by day, anything longer is read month by month.
 *
 * `week` is NOT in the automatic ladder any more. It remains a real, selectable unit in
 * the "View by" control — `ANALYTICS_BUCKET_UNITS` is unchanged — it is simply never the
 * unit nobody chose. A weekly bucket answers a question ("which weeks were busy") that a
 * reader can ask for; it is a poor DEFAULT because the week boundary is an arbitrary
 * artefact of the range's start date, so two readers with ranges a day apart get marks
 * that do not line up while both charts claim to be weekly.
 *
 * ⚠️ THIS DIVERGES FROM `roiValueGranularity`, DELIBERATELY, AND THAT USED TO BE FORBIDDEN.
 * The previous docblock argued the thresholds must be shared because the ROI chart and the
 * scans chart sit on the same page, and two charts stepping at different range lengths
 * would invite the reader to compare a weekly line against a daily one. That argument no
 * longer holds, for two reasons:
 *
 *   1. VRG-512 itself retired the invariant it depends on. Granularity is now a PER-CARD
 *      display property (`analyticsCardGranularity`) — a reader can already put one chart
 *      on Day while another sits on Month, and that module's header defends this as
 *      correct. "Every chart on the page shares a bucket" stopped being true the moment
 *      the control shipped; only the DEFAULTS ever agreed, and they agreed by accident of
 *      implementation rather than by a property anyone could see.
 *   2. The two series are not the same kind of measurement. This one counts EVENTS from
 *      Postgres rows and is dense; the ROI series is MONEY derived from settled invoices
 *      and is sparse, which is what its weekly middle step is for. One ladder serving both
 *      was convenient, not principled.
 *
 * The alternative — changing `roiValueGranularity` so both step at 3 months — was
 * considered and rejected as materially larger than the change asked for: it is a SERVER
 * function, it would re-bucket a money series onto a finer grid in the 46–92 day band, and
 * the spend series is bucket-aligned to it (see `invoice-spend.guard.spec.ts`). If product
 * wants the ROI hero on the same ladder, that is its own change with its own verification.
 *
 * ── RANGE LENGTH ONLY. NOT DATA VOLUME ───────────────────────────────────────────────
 *
 * "Pick the bucket from how much data they have" is the obvious-sounding alternative and it
 * is wrong three times over. It makes the same range render differently for two brands, so
 * no screenshot or support answer generalises. It makes a chart RE-BUCKET AS DATA ARRIVES
 * — a brand crossing a volume threshold watches its chart change shape with no control
 * touched — which is the identical failure `defaultMarkFor` rejects for the mark. And it
 * is circular: you cannot know the volume without first fetching at some bucket, so it
 * costs a second request and a visible redraw to answer a question the range already
 * answers. Range length determines the NUMBER OF MARKS, which is the thing that actually
 * decides whether a chart is readable; `ANALYTICS_MIN_BUCKETS`/`ANALYTICS_MAX_BUCKETS`
 * already gate the two ends of that, deterministically and from the range alone.
 *
 * Measured in WHOLE DAYS rather than raw milliseconds, matching `roiValueGranularity`'s own
 * shape: this codebase has already shipped one range-arithmetic bug from ceiling-ing raw
 * milliseconds, and a boundary that lands differently depending on the time of day is the
 * same class of defect.
 *
 * A zero-or-negative span falls to `'day'` rather than `'hour'`: an inverted or empty
 * range is not a short one, and asking for hourly buckets over it would be a request
 * built from a nonsense premise.
 */

/**
 * Range length at which the automatic bucket steps from day up to month, in whole days.
 *
 * ⚠️ 93 IS MEASURED FROM THE DATE PICKER'S OWN "LAST 3 MONTHS" PRESET, NOT REASONED FROM
 * A CALENDAR — and the difference is not academic, it was wrong once. The first cut used
 * 92, on the sound-looking argument that the longest real quarter (Jul–Sep) is 92 days.
 * Driven in the browser, the preset labelled "Last 3 months" produced `Month`: the picker
 * stores the range END-OF-DAY inclusive, so Jun 7 00:00 → Sep 7 23:59 rounds to 93, one
 * over the threshold.
 *
 * That is the precise case the rule exists for. A reader who clicks the control that says
 * "Last 3 months" and is told the answer is monthly has been contradicted by the product's
 * own vocabulary, and no amount of calendar arithmetic makes that right. So the threshold
 * is calibrated to the control rather than to the abstraction behind it: the boundary is
 * whatever "3 months" means to the thing the user actually clicks.
 *
 * `Last 6 months` (~184 days) is comfortably above it and still resolves to `month`, so
 * the two adjacent presets land on the two different answers, which is the behaviour the
 * rule is for.
 */
export const ANALYTICS_DAILY_MAX_DAYS = 93

export const analyticsChartGranularity = (from: Date, to: Date): AnalyticsChartBucketUnit => {
  const spanMs = to.getTime() - from.getTime()
  if (spanMs > 0 && spanMs <= ANALYTICS_HOURLY_MAX_SPAN_MS) return 'hour'
  const days = Math.round(Math.max(0, spanMs) / 86_400_000)
  return days <= ANALYTICS_DAILY_MAX_DAYS ? 'day' : 'month'
}

/* ────────────────────────────────────────────────────────────────────────────────────
   BUCKET WIDTH AS A USER-FACING CHOICE — the "View by:" control (VRG-512)
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * The bucket widths a reader can ask for, as a UNIT rather than as request parameters.
 *
 * ⚠️ IT EXTENDS `RoiValueGranularity` RATHER THAN RESTATING IT. `'day' | 'week' | 'month'`
 * is the ROI value series' own vocabulary and the two charts sit on the same page, so a
 * second three-member union with the same members would be the drift §36 is about — and
 * worse than a duplicated constant, because a reader comparing the two charts would have
 * no way to tell whether "week" meant the same thing on each. `hour` and `year` are the
 * two ends this chart has and that one does not: it counts EVENTS, so an afternoon is a
 * real question, and its range can be opened to several years where a money series is
 * bounded by the billing history.
 *
 * ⚠️ A UNIT IS NOT A `Granularity`. See `analyticsBucketRequest` — `week` and `year` are
 * NOT server enum members and are expressed through `interval_count`.
 */
export type AnalyticsChartBucketUnit = 'hour' | RoiValueGranularity | 'year'

/** Offered in the control in this order — coarsening left to right. */
export const ANALYTICS_BUCKET_UNITS: readonly AnalyticsChartBucketUnit[] = [
  'hour',
  'day',
  'week',
  'month',
  'year',
]

export const ANALYTICS_BUCKET_UNIT_LABELS: Record<AnalyticsChartBucketUnit, string> = {
  hour: 'Hour',
  day: 'Day',
  week: 'Week',
  month: 'Month',
  year: 'Year',
}

/**
 * A unit, as the two request parameters that express it.
 *
 * 🚨 `week` AND `year` ARE REAL SERVER-SIDE BUCKETS, NOT CLIENT RE-BUCKETING. This is the
 * distinction that matters most in this module, because the alternative is the one thing
 * that would make the chart and its CSV disagree.
 *
 * The server's `Granularity` enum is `hour | day | month` and REJECTS `week` and `year`
 * outright (`@IsEnum(Granularity)` on the DTO — verified against the running API, both
 * return "Validation errors"). But the interval it builds is `'<count> <granularity>'`,
 * interpolated into the `generate_series` spine AND into every row-attach predicate, so:
 *
 *   week -> `7 day`      year -> `12 month`
 *
 * are intervals Postgres bucket natively — also verified against the running API (a
 * 2-year range returns 105 and 3 buckets respectively). So the SERVER still does the
 * bucketing and the response still carries one row per bucket. Nothing is summed on the
 * client, which is what keeps the exported CSV identical to the plotted series; folding
 * seven daily rows together in the chart would have produced a file whose rows no longer
 * matched the marks above them, and because the totals would still agree, nothing would
 * have looked wrong.
 *
 * ⚠️ DO NOT "TIDY" week INTO A NEW `Granularity.Week`. It would need an enum member, a DTO
 * value and new SQL to express something `interval_count` already says, and it would
 * break every client that pins the current enum.
 */
export const analyticsBucketRequest = (unit: AnalyticsChartBucketUnit): AnalyticsChartBucket => {
  switch (unit) {
    case 'hour':
      return { interval_granularity: 'hour', interval_count: 1 }
    case 'week':
      return { interval_granularity: 'day', interval_count: 7 }
    case 'month':
      return { interval_granularity: 'month', interval_count: 1 }
    case 'year':
      return { interval_granularity: 'month', interval_count: 12 }
    default:
      return { interval_granularity: 'day', interval_count: 1 }
  }
}

/** Nominal bucket length. Month/year are AVERAGES — see `analyticsBucketCount`. */
const BUCKET_MS: Record<AnalyticsChartBucketUnit, number> = {
  hour: 60 * 60 * 1000,
  day: 24 * 60 * 60 * 1000,
  week: 7 * 24 * 60 * 60 * 1000,
  month: (365.25 / 12) * 24 * 60 * 60 * 1000,
  year: 365.25 * 24 * 60 * 60 * 1000,
}

/**
 * Roughly how many buckets a unit yields over a range.
 *
 * ⚠️ APPROXIMATE FOR month/year ON PURPOSE, and it is only ever used to decide whether an
 * option is OFFERED — never to interpret data. Calendar months are 28–31 days, so an exact
 * count needs real date arithmetic; a mean-length divisor is off by at most a bucket, and
 * a threshold of 2 or 400 does not turn on one bucket. Using it to label or aggregate
 * anything would be a different matter entirely: the server's own bucketing is
 * authoritative and this must never be used to second-guess it.
 */
export const analyticsBucketCount = (
  from: Date,
  to: Date,
  unit: AnalyticsChartBucketUnit,
): number => {
  const spanMs = to.getTime() - from.getTime()
  if (!(spanMs > 0)) return 0
  return Math.max(1, Math.ceil(spanMs / BUCKET_MS[unit]))
}

/**
 * A chart needs at least this many buckets to BE a chart.
 *
 * 2, because one bucket is a single mark: no line, no shape, no trend — the reader gets a
 * number rendered as a chart, which is strictly worse than the number. "View by year" on a
 * 12-month range is exactly this case and is the reason the floor exists rather than a
 * theoretical edge.
 */
export const ANALYTICS_MIN_BUCKETS = 2

/**
 * And at most this many before the marks stop resolving.
 *
 * 400 is a MEASURED ceiling rather than a round number: the chart's plot area is ~1,000px
 * wide on a 1280px viewport (`Layout`'s 24px gutters, the sidebar, the card's own
 * padding), so 400 buckets is ~2.5px per mark — about the narrowest a reader can still
 * tell two adjacent points apart. Past it, adjacent marks share a pixel column and the
 * line becomes a smear: ~1,800 daily points over five years is the case that motivates the
 * cap, and ~17,500 hourly points over two years is what an ungated control would let
 * someone ask for.
 *
 * ⚠️ IT DELIBERATELY LEAVES "day OVER A YEAR" (~365) AVAILABLE, one notch under the cap.
 * That is the exact view this ticket's automatic stepping exists to steer people AWAY
 * from — but steering is not the same as forbidding. Auto picks month for a year; a reader
 * who explicitly wants the daily detail is answering a different question and is entitled
 * to it. A control that refused would be substituting its judgement for theirs, which is
 * the failure mode that separates a granularity control from the smoothing knob that was
 * rejected.
 */
export const ANALYTICS_MAX_BUCKETS = 400

export interface AnalyticsBucketOption {
  unit: AnalyticsChartBucketUnit
  label: string
  enabled: boolean
  /** Why not, in the reader's terms. Present iff `!enabled` — §46's forcing function. */
  reason?: string
}

/**
 * Every unit with whether this range can be viewed that way, and why not when it cannot.
 *
 * ⚠️ THE UNAVAILABLE ONES ARE RETURNED, NOT FILTERED OUT. §46: a control whose options
 * appear and disappear as the date picker moves reads as a bug, and the reader is left
 * guessing whether "Year" was ever there. Disabled-with-a-reason says the option exists,
 * says what would make it work, and keeps the control the same shape at every range —
 * which is also why the reason is a real sentence naming the fix ("Select a longer range
 * to view by year") rather than a restatement of the state.
 */
export const analyticsBucketOptions = (from: Date, to: Date): AnalyticsBucketOption[] =>
  ANALYTICS_BUCKET_UNITS.map(unit => {
    const count = analyticsBucketCount(from, to, unit)
    const label = ANALYTICS_BUCKET_UNIT_LABELS[unit]
    if (count < ANALYTICS_MIN_BUCKETS) {
      return {
        unit,
        label,
        enabled: false,
        reason: `Select a longer range to view by ${label.toLowerCase()}.`,
      }
    }
    if (count > ANALYTICS_MAX_BUCKETS) {
      return {
        unit,
        label,
        enabled: false,
        reason: `Too many points to plot. Select a shorter range to view by ${label.toLowerCase()}.`,
      }
    }
    return { unit, label, enabled: true }
  })

/**
 * The unit actually used: the reader's choice when the range can honour it, else auto.
 *
 * ⚠️ THE FALLBACK IS SILENT HERE AND MUST NOT BE SILENT ON SCREEN. The coordinator's rule
 * is that an override survives a range change rather than snapping back, so `override` is
 * deliberately NOT cleared when it becomes unusable — the reader gets it back the moment
 * they widen the range again. The cost is that the chart is briefly drawn at a unit nobody
 * asked for, so the CONTROL is responsible for saying so (it keeps the chosen row checked
 * and disabled, and captions the trigger with what is actually being drawn). Returning the
 * fallback from here without that caption would be the silent re-derivation §46 warns
 * about.
 */
export const analyticsEffectiveBucketUnit = (
  from: Date,
  to: Date,
  override: AnalyticsChartBucketUnit | null,
): AnalyticsChartBucketUnit => {
  if (!override) return analyticsChartGranularity(from, to)
  const option = analyticsBucketOptions(from, to).find(o => o.unit === override)
  return option?.enabled ? override : analyticsChartGranularity(from, to)
}

/** The request parameters for a range + the reader's choice. The one call sites want. */
export const analyticsChartBucketParams = (
  from: Date,
  to: Date,
  override: AnalyticsChartBucketUnit | null,
): AnalyticsChartBucket =>
  analyticsBucketRequest(analyticsEffectiveBucketUnit(from, to, override))

/* ────────────────────────────────────────────────────────────────────────────────────
   6. THE MAP'S SECOND AXIS: GROUPING (VERA-260)
   ──────────────────────────────────────────────────────────────────────────────────── */

/**
 * ── "BY COUNTRY" IS NOT A DIFFERENT CARD. IT IS THE MAP AT A DIFFERENT GROUPING ─────
 *
 * The page currently ships two map cards that are really one card at two granularities:
 *
 * | card | what one mark is | source |
 * |---|---|---|
 * | `ScansByLocation` (a `::map` variant) | one located EVENT | `getScanMapPoints` |
 * | `ScansByCountry` (`scans_by_country`) | one COUNTRY, filled | `getCountryMetrics` |
 *
 * Both answer "where". They differ in what they group the events INTO — a point or a
 * polygon — which is the same relationship the leaderboard's `Group by:` has to its rows,
 * and the same relationship `rank_by` has to `dimension` there. The split into two library
 * rows is a build-order artefact: the choropleth landed after the pin map and needed
 * somewhere to live, so it became a card instead of a mode.
 *
 * That artefact has a cost, and it is the reason this section exists rather than a comment
 * on the two cards. With grouping welded to the card, the question "can THIS metric be
 * drawn by country?" has no home — exactly the shape of the bug `MAP_RENDERABLE`'s own
 * docblock records about itself (a narrow key that made the question unaskable, so a
 * capable dimension fell through to a default that read as a decision and was an accident).
 * `invalid_scans` is the live instance a second time over: the country endpoint has
 * returned a per-country `invalid_scans` COUNT since the day it shipped, and no record
 * anywhere said whether the map could paint it. Nothing said no. Nothing said yes.
 *
 * So grouping becomes its own axis, with its own total capability record, and the two
 * render surfaces become two groupings of one question.
 *
 * ⚠️ WHAT THIS SECTION DOES **NOT** DO, stated here so the next reader does not infer a
 * capability from a record that has one. It declares the axis and answers it for every
 * metric. It does NOT yet rewire the render layer: `scans_by_country` is still its own
 * card id, `ScansByLocation` still has no grouping toggle, and no drop onto a map is
 * accepted. Those are the follow-up, and doing them in the same pass as the classification
 * would have meant shipping a grouping switch whose gating had never been reviewed. A
 * half-built model that silently paints the wrong aggregate is worse than either half
 * alone — see `certificates` below for the specific aggregate that would have been wrong.
 */
export type AnalyticsMapGrouping = 'pin' | 'country'

/**
 * Every metric the map axis has to answer for — the COUNT dimensions and the VALUE
 * dimensions together.
 *
 * ⚠️ IT MUST BE BOTH UNIONS, and the pin map is why that is not obvious. A pin map only
 * ever needed `AnalyticsSeriesDimension`, because a rate has no event to place (see
 * `analyticsDisplayCapability`'s value-path branch, which returns a map refusal saying
 * exactly that). A CHOROPLETH does not have that limit — colouring an area by a rate is
 * the canonical use of one — and `NO_DIMENSION_REASON.map` already predicted this in
 * prose: "the right render for all three is a per-country choropleth... it earns its own
 * capability record keyed on whatever dimension set the country endpoint can group". This
 * is that record, and the key is the union of the two.
 *
 * The two unions are DISJOINT by construction (`store/analytics/types` asserts it), so
 * this is a flat 13-member key with no overlap to disambiguate, and a `Record` over it is
 * a compile error the moment EITHER union gains a member.
 */
export type AnalyticsMapMetric = AnalyticsSeriesDimension | AnalyticsValueDimension

/**
 * How a country's fill is computed from one row of the country aggregate.
 *
 * ⚠️ EXECUTABLE, NOT PROSE, and that is the point — `MAP_DIMENSION_SCAN_STATUSES` beside
 * it is the same discipline for the pin map's requests. A capability record whose "yes"
 * is a bare `true` and whose supporting query lives in a comment is how a record comes to
 * promise something no code issues; `keyof ICountryMetricRow` means a renamed or dropped
 * column is a compile error here rather than an unpainted map.
 *
 * A rate carries its DENOMINATOR because the choropleth may not render a percentage
 * without one — 1-of-2 and 400-of-800 are both "50%" and only one of them means anything,
 * which is the rule `MapChoroplethDatum` and the small-sample floor
 * (`COUNTRY_METRIC_DEFAULT_MIN_DENOMINATOR`) exist to enforce. A count states itself as
 * both halves, exactly as `ScansByCountry`'s own `METRICS.scans` already does.
 */
/**
 * The MEASURE columns of a country row — every key whose value is a number.
 *
 * ⚠️ NARROWED FROM `keyof ICountryMetricRow` (VERA-260, render pass), and the narrowing is
 * the same discipline the descriptor was introduced for rather than a tidy-up. The wide
 * key admits `code` and `name`, so `{ kind: 'count', numerator: 'name' }` type-checked —
 * a descriptor that promises a fill and hands the renderer a string. The renderer would
 * then either coerce it (a country painted `NaN`, or worse, silently 0) or need a runtime
 * guard whose failure branch invents a number nobody authored. Making it a compile error
 * is what the executable descriptor was for; this closes the half the wide key left open.
 */
type CountryMetricMeasure = {
  [K in keyof ICountryMetricRow]: ICountryMetricRow[K] extends number ? K : never
}[keyof ICountryMetricRow]

export type CountryGroupingValue =
  | { kind: 'count'; numerator: CountryMetricMeasure }
  | {
      kind: 'rate'
      numerator: CountryMetricMeasure
      denominator: CountryMetricMeasure
      /** The denominator as a plural noun, for the legend's small-sample swatch. NOT
       *  always "scans" — the repeat rate divides by CERTIFICATES, and saying otherwise
       *  named a threshold none of the suppressed countries had actually failed. */
      denominatorNoun: string
    }

/**
 * ── CAN THIS METRIC BE GROUPED BY COUNTRY? ──────────────────────────────────────────
 *
 * TOTAL over `AnalyticsMapMetric`, so a new count or a new statistic is a compile error
 * here until someone says whether a country can be filled by it — the discipline
 * `MAP_RENDERABLE` and `TABLE_RANKABLE` already follow, on the axis this file was missing.
 *
 * The bound is `getCountryMetrics`' returned row, which carries exactly four measures:
 * `scans`, `certificates`, `invalid_scans`, `repeat_certificates`, grouped from `scans`
 * JOINed to `certificates` — the SAME join the pin map uses, so a metric capable at both
 * groupings is counting one population two ways rather than two populations.
 *
 * ⚠️ ONE POPULATION DIFFERENCE IS REAL AND IS NOT A BUG. The pin query requires
 * `geo_lat IS NOT NULL AND geo_lng IS NOT NULL`; the country query requires only a
 * `geo_country` that resolves. A scan that geoip placed in a country without a coordinate
 * is therefore counted by country and not pinned, so a country fill can legitimately
 * exceed the pins drawn inside it. That is the correct behaviour for the two granularities
 * — a polygon needs a country, a point needs a point — and the card already prints the two
 * gap figures (`unresolved`, `unattributed_scans`) that make the remainder visible.
 */
const COUNTRY_GROUPABLE: Record<
  AnalyticsMapMetric,
  | { capable: true; value: CountryGroupingValue }
  | { capable: false; reason: string }
> = {
  /* ── The three that the aggregate answers directly ──────────────────────────────── */

  // Already shipped as the choropleth's `scans` metric.
  scans: { capable: true, value: { kind: 'count', numerator: 'scans' } },

  /*
    TRUE, and it is the entry this whole section was worth writing for.

    `row.invalid_scans` has been in the payload since the endpoint shipped, computed by
    the same `FILTER (WHERE "s"."invalid")` the pin map's `scan_statuses: ['invalid']`
    selects on, over the same joined rows. So "invalid scans as pins" and "invalid scans
    by country" are one measurement at two granularities — the model's clean case, and the
    thing that makes grouping a real axis rather than a relabelling of two cards.

    The card exposes it only as a RATE today (`invalid_rate`), which is a different
    question: a rate says how BAD a market is, a count says how MUCH of the problem is
    there. A country with 4 invalid scans out of 5 and one with 400 out of 10,000 are the
    two ends of that, and a brand chasing counterfeits wants both.
  */
  invalid_scans: { capable: true, value: { kind: 'count', numerator: 'invalid_scans' } },

  // Already shipped as the choropleth's `invalid_rate` metric.
  invalid_scan_rate: {
    capable: true,
    value: {
      kind: 'rate',
      numerator: 'invalid_scans',
      denominator: 'scans',
      denominatorNoun: 'scans',
    },
  },

  /*
    Already shipped as the choropleth's `repeat_rate`.

    ⚠️ Country-SCOPED, so it is a sibling of the page's global repeat-scan rate rather
    than a decomposition of it: a certificate scanned once in France and once in Japan is
    a repeat globally and in neither country, so these rows will not reconcile to the
    figure on the card beside them. `ICountryMetricRow.repeat_certificates` carries the
    full reasoning for why that is the honest choice over attributing a globally-repeat
    item to every country it was seen in. Capable, with a caveat the card must keep
    printing — not incapable.
  */
  repeat_scan_rate: {
    capable: true,
    value: {
      kind: 'rate',
      numerator: 'repeat_certificates',
      denominator: 'certificates',
      denominatorNoun: 'items scanned there',
    },
  },

  /* ── The refusals ───────────────────────────────────────────────────────────────── */

  /*
    ⚠️ FALSE, AND THE COLUMN EXISTS — which makes this the most dangerous entry in the
    record and the reason it is a considered `false` rather than the obvious `true`.

    `row.certificates` is right there in the payload. Wiring it would compile, render, and
    be WRONG in the specific way this whole model is supposed to prevent: the two groupings
    would not be one measurement at two granularities, they would be two measurements
    sharing a name.

      - AT PIN GROUPING, `certificates` is `mode: 'latest'` — `ROW_NUMBER() ... rn = 1`.
        Each certificate contributes exactly ONE mark, at its most recent location. The
        marks sum to the number of certificates.
      - AT COUNTRY GROUPING, `row.certificates` is distinct certificates SCANNED IN that
        country. An item seen in three countries is counted in all three. The fills sum to
        MORE than the number of certificates.

    So a reader toggling pin -> country on "Certificates" would watch the total silently
    increase, with nothing on screen saying the question had changed. That is precisely
    the "silently plots the wrong aggregate" failure, and it is worse than an absence
    because it is arithmetically self-consistent on both sides of the toggle.

    THE FIX IS SMALL AND IS SERVER-SIDE, so this is a `false` with a remedy rather than a
    permanent one: group the country aggregate on the country of each certificate's LATEST
    scan (the same `rn = 1` the pin map already uses) and the two groupings agree by
    construction. It is not done here because `per_country_certificate` is also what
    `repeat_certificates` is computed from, and narrowing it to one row per certificate
    would silently redefine the repeat rate the card already ships. Two changes, one CTE,
    and only one of them is in scope.
  */
  certificates: {
    capable: false,
    reason:
      'A country fill would count an item once for every country it has been scanned in, while the pin map places each item once at its latest location. The two would disagree, so this is not offered until the per-country figure is grouped on each item’s latest scan.',
  },

  /*
    ⚠️ THE USER'S OWN EXAMPLE, answered plainly: RESALES BY COUNTRY CANNOT BE HONEST TODAY.

    Not "not built". There is nothing to group on. `getCountryMetrics` groups
    `scans.geo_country`; a resale is a row in `certificate_action_logs` with no `geo_*`
    column, no coordinate, and no row-level link to the scan that accompanied it. The
    aggregate has no resale column because the query has no way to produce one.

    The only available route is "via the certificate's scans" — the `scanGeoExists`
    approximation this epic has now rejected THREE times, and it fails worse at country
    grouping than at either previous site:

      1. For scan OUTCOME (`analytics.postgres.repository.ts`): an invalid scan is by
         nature the exception on an otherwise-verified certificate, so "every scan of a
         certificate that has one" returns the whole history as the invalid count.
      2. For the LOSS REPORTS (`MAP_LOCATED` above): measured on dev — 3 `stole` rows
         against 214 located scans on the one stolen certificate. A card reading 3 would
         have drawn 214 marks. Two orders of magnitude.
      3. HERE, and this is the new failure the first two do not have: a certificate
         scanned in five countries would contribute its resale to ALL FIVE. The per-country
         resale counts would sum to several times the resale total on the card beside them.
         A choropleth whose columns do not sum to its own headline figure is not an
         approximation, it is a different number wearing the same title.

    An honest resales-by-country is real work and a real product decision, not a filter: it
    needs the resale itself to carry a place — the country of the scan nearest in time to
    the transfer, or the buyer's address — which is a new aggregate AND a decision about
    what that fill MEANS. Until one exists, `false` is the true answer rather than the
    unfinished one.
  */
  resales: {
    capable: false,
    reason:
      'A resale is not recorded with a place. Locating one through the item’s scans would count it in every country that item was ever seen in, so the countries would add up to several times the resale total.',
  },

  /*
    The same structural absence, one table over, and already stated by `MAP_LOCATED`: a
    transfer and a loss report are records of an OWNERSHIP event rather than of a device
    touching a tag, so no row involved carries a place. Grouping cannot invent one, so
    these are false at BOTH groupings for one reason — which is the shape a reader should
    expect, since "has no location" is a fact about the data model and grouping is a fact
    about our queries.
  */
  transfers: {
    capable: false,
    reason:
      'A transfer is not recorded with a place, so there is nothing to group into a country.',
  },
  stolen_reports: {
    capable: false,
    reason:
      'A theft report is not recorded with a place, so there is nothing to group into a country.',
  },
  lost_reports: {
    capable: false,
    reason:
      'A loss report is not recorded with a place, so there is nothing to group into a country.',
  },

  /*
    The remaining value dimensions. Each is a statistic the country aggregate does not
    compute and could not compute from its four measures — this is a missing COLUMN, said
    plainly, rather than a claim that the underlying events have no location. Two of the
    three are resale-derived and so also inherit the resale problem above; the median is
    scan-derived and genuinely could be added to the aggregate one day, which is why its
    sentence does not claim otherwise.
  */
  avg_resale_price_cents: {
    capable: false,
    reason:
      'A resale is not recorded with a place, so there is no country to attribute a resale price to.',
  },
  resales_per_resold_item: {
    capable: false,
    reason:
      'A resale is not recorded with a place, so there is no country to attribute it to.',
  },
  certificates_per_owner: {
    capable: false,
    reason:
      'This divides items by owners, and an owner is not tied to one country, so the per-country figure would have no denominator.',
  },
  median_days_to_first_scan: {
    capable: false,
    reason:
      'The per-country figures do not include how long items took to be scanned, so there is nothing to fill a country with.',
  },
}

/** The metric a card maps, across BOTH unions, or `null` when it addresses neither. */
const analyticsCardMapMetric = (cardId: string): AnalyticsMapMetric | null => {
  const dimension = analyticsCardDimension(cardId)
  if (dimension && isSeriesDimension(dimension)) return dimension
  return analyticsCardValueDimension(cardId)
}

/**
 * Can this card be drawn on a map at this grouping, and if not, why not?
 *
 * ⚠️ THE `pin` ANSWER IS DELEGATED, NEVER RE-DERIVED — to `analyticsMapPinCapability`,
 * the conjunction of `MAP_LOCATED` and `MAP_RENDERABLE` plus the value path's own refusal,
 * because a second expression of that rule is precisely the drift this file keeps
 * recording about itself.
 *
 * ⚠️ THE DELEGATION TARGET MOVED (VERA-260, render pass). It used to be
 * `analyticsDisplayCapability(cardId).map`, which was correct while `.map` WAS the pin
 * answer. It no longer is — `.map` is now the UNION over groupings, built on top of this
 * function — so delegating there would be circular and would report every rate as
 * pin-capable. One level down is the same single expression, just below the union rather
 * than above it.
 *
 * Only the `country` column consults `COUNTRY_GROUPABLE`.
 */
export const analyticsMapGroupingCapability = (
  cardId: string,
  grouping: AnalyticsMapGrouping,
): DisplayCapability => {
  if (grouping === 'pin') return analyticsMapPinCapability(cardId)

  const metric = analyticsCardMapMetric(cardId)
  if (!metric) {
    // No addressable metric at all — the same answer the pin map gives, and for the same
    // reason: there is no per-event row behind this card for any grouping to collect.
    return { capable: false, reason: NO_DIMENSION_REASON.map }
  }
  const entry = COUNTRY_GROUPABLE[metric]
  return entry.capable ? CAPABLE : { capable: false, reason: entry.reason }
}

/**
 * ── WHY A CARD CANNOT BE A MAP AT ALL, in the reader's terms ────────────────────────
 *
 * Reached only when BOTH groupings refuse. Which of the two sentences to serve is not
 * obvious, and taking the pin's unconditionally was wrong — it was caught once already by
 * printing the matrix rather than by reading the code, and the reasoning is preserved
 * here because it now has two call sites (`analyticsMapCapability` and the compose
 * refusal) rather than one:
 *
 * For a COUNT, the pin refusal is about the DATA ("Resales are not stored with a
 * location"), so it holds at every grouping and is the right answer.
 *
 * For a VALUE — a rate, a mean, a median — the pin refusal is about the PIN: "a pin can
 * only show that an event happened". That sentence invites the reader to infer that a
 * COARSER grouping would work. For `invalid_scan_rate` and `repeat_scan_rate` the
 * inference is CORRECT — they are `COUNTRY_GROUPABLE` and this function is never reached
 * for them. For the four that do reach it, the inference is wrong, and the country
 * refusal is the one that names the actual obstacle (a resale has no place at all; the
 * aggregate has no such column). Serving the pin's sentence there would be a
 * plausible-looking falsehood on the default branch.
 */
const mapRefusalReason = (
  cardId: string,
  pin: DisplayCapability,
  country: DisplayCapability,
): string => {
  const metric = analyticsCardMapMetric(cardId)
  const value = metric != null && !isSeriesDimension(metric)
  const chosen = value ? country : pin
  // Both are refusals by construction at every call site, but the type does not know it.
  return chosen.capable ? NO_DIMENSION_REASON.map : chosen.reason
}

/**
 * ── CAN THIS CARD BE A MAP, AT ANY GROUPING? ────────────────────────────────────────
 *
 * The `map` column of `analyticsDisplayCapability`, and therefore what the library's
 * `Display as -> Map` chip and its type filter both read. Capable when EITHER grouping can
 * draw it; the refusal is `mapRefusalReason`'s pick of the two honest sentences.
 *
 * ⚠️ A `function` DECLARATION, not the `const` arrow this file uses everywhere else, and
 * the exception is mechanical rather than stylistic. `analyticsDisplayCapability` calls
 * this, and this reads `COUNTRY_GROUPABLE`, which is declared ~350 lines BELOW it. A
 * `const` arrow would be in its temporal dead zone for anything that ran during module
 * evaluation; hoisting removes the hazard outright rather than relying on the fact that
 * nothing currently calls the capability at import time. (`analyticsViewSeeds` DOES build
 * card ids at module scope, which is exactly the shape of call that would trip it, so the
 * hazard is not hypothetical for this file's neighbourhood.)
 */
export function analyticsMapCapability(cardId: string): DisplayCapability {
  const pin = analyticsMapGroupingCapability(cardId, 'pin')
  if (pin.capable) return CAPABLE
  const country = analyticsMapGroupingCapability(cardId, 'country')
  if (country.capable) return CAPABLE
  return { capable: false, reason: mapRefusalReason(cardId, pin, country) }
}

/**
 * The grouping a map card OPENS at.
 *
 * Pins where pins work, because one mark per event is the more specific answer and the
 * one this page has always given; a country fill where they do not, which is the whole
 * reason a rate has a map at all now. Only meaningful for a card `analyticsMapCapability`
 * reports capable — for an incapable one it returns `'country'` and nothing ever asks.
 *
 * ⚠️ DERIVED, never authored per metric. A hardcoded default list is the flag this file's
 * header forbids, and it would go stale in the direction that lies: the day
 * `certificates` becomes country-groupable (see its entry — the fix is server-side and
 * named), its default must not silently move.
 */
export const analyticsDefaultMapGrouping = (cardId: string): AnalyticsMapGrouping =>
  analyticsMapGroupingCapability(cardId, 'pin').capable ? 'pin' : 'country'

/**
 * The variant id a `Display as` chip should place — the one place the grouping default is
 * applied when BUILDING an id.
 *
 * Non-map displays are unchanged and pass no grouping, so their ids stay byte-identical.
 * A map's id carries `@country` only when pins cannot draw the metric, which is what makes
 * the Map chip on a rate card place a choropleth rather than a card that refuses to render.
 */
export const analyticsDefaultVariantCardId = (
  cardId: string,
  display: AnalyticsCardCategory,
): string =>
  analyticsVariantCardId(
    cardId,
    display,
    display === 'map' ? analyticsDefaultMapGrouping(cardId) : undefined,
  )

/**
 * ── IS THIS VARIANT CARD A LIBRARY PRESET? ──────────────────────────────────────────
 *
 * The library lists one row per METRIC and offers each metric's displays through its
 * `Display as` chips, so variant ids are filtered out of it. A preset is the exception
 * that rule always implied: a variant NO CHIP CAN PRODUCE.
 *
 * There is exactly one shape of those today. A `Display as -> Map` chip places the map at
 * its DEFAULT grouping (`analyticsDefaultVariantCardId`) and there is no per-grouping
 * chip, so a map at the metric's NON-default grouping is unreachable from the panel — and
 * that is precisely `scans_count::map@country`, the card `scans_by_country` became.
 *
 * ⚠️ DERIVED FROM CAPABILITY, never a list of ids. A hardcoded `['scans_count::map@country']`
 * would be the flag this file's header forbids, and it would go stale in the direction
 * that lies: the day `certificates` becomes country-groupable (the fix is server-side and
 * its `COUNTRY_GROUPABLE` entry names it) it acquires a preset row with no edit anywhere,
 * and a metric that loses its pin grouping stops being a preset the same way — because
 * its country map would then be the default the chip already places.
 */
export const analyticsIsLibraryPresetCardId = (cardId: string): boolean => {
  const parsed = parseAnalyticsVariantCardId(cardId)
  if (!parsed || parsed.display !== 'map') return false
  // A COMPOSED id is never a preset: it is user-created by the drag gesture, not offered.
  if (parsed.metricIds.length > 1) return false
  return parsed.grouping !== analyticsDefaultMapGrouping(parsed.baseId)
}

/** How a country's fill is computed for this card, or `null` when it cannot be one. */
export const analyticsCountryGroupingValue = (
  cardId: string,
): CountryGroupingValue | null => {
  const metric = analyticsCardMapMetric(cardId)
  if (!metric) return null
  const entry = COUNTRY_GROUPABLE[metric]
  return entry.capable ? entry.value : null
}

/**
 * What the map's colour MEANS, for the choropleth legend — the metric's own label, from
 * whichever union it belongs to.
 *
 * Not the CARD's title: a card is titled "Total Scans" and a legend saying "Total Scans"
 * over a per-country scale reads as the page total rather than as this country's value.
 */
export const analyticsMapMetricLabel = (cardId: string): string => {
  const metric = analyticsCardMapMetric(cardId)
  if (!metric) return 'Value'
  return isSeriesDimension(metric) ? dimensionLabel(metric) : analyticsValueLabel(metric)
}

/**
 * ── THE ONE THING A READER WILL OTHERWISE CALL A BUG ────────────────────────────────
 *
 * A country's fill can legitimately EXCEED the number of pins drawn inside it. The pin
 * query requires `geo_lat IS NOT NULL AND geo_lng IS NOT NULL`; the country query requires
 * only a `geo_country` that resolves. A scan geoip placed in a country without a
 * coordinate is therefore counted by country and never pinned.
 *
 * That is correct at both granularities — a polygon needs a country, a point needs a point
 * — and it is exactly the kind of correct-but-surprising arithmetic that reads as a defect
 * the first time someone flips the toggle and watches the number grow. So it is SAID, at
 * the control that causes it, rather than left in `COUNTRY_GROUPABLE`'s docblock where
 * only the next engineer would meet it.
 *
 * ONE sentence, one home. The `Group by:` tooltip and the choropleth's own footnote both
 * read it, because two copies of a caveat drift and the drifted one is the one nobody
 * re-checks (§44's argument, one layer down from the glossary).
 */
export const ANALYTICS_MAP_GROUPING_CAVEAT =
  'A country can count more than the pins drawn inside it: a pin needs exact coordinates, a country fill only needs the country. Scans located to a country but no coordinate are counted here and not pinned.'

/**
 * ⚠️ THE ONE PLACE "WHICH DISPLAY COMPOSES" IS DECIDED — and it used to be decided in two.
 *
 * `AnalyticsCanvas.isComposeDropTarget` had `parsed.display !== 'chart'` and
 * `buildComposedDisplayVariantCard` had its own `parsed.display !== 'chart'`, each under a
 * paragraph explaining a premise that had already expired (both described a map that could
 * plot exactly one dimension, which `MAP_RENDERABLE` outgrew). Two expressions of one rule
 * is how they came to disagree about the world while agreeing about the boolean. Both now
 * derive from here: the canvas via `analyticsComposeTargetRefusal` (which needs the
 * SENTENCE), the builder via this predicate (which needs only the BOOLEAN).
 */
export const analyticsComposedDisplaySupported = (
  display: AnalyticsCardCategory,
): boolean => display === 'chart'

/**
 * ── WHY A METRIC CANNOT BE DROPPED ONTO A MAP CARD, in the reader's terms ───────────
 *
 * ⚠️ THIS IS THE SINGLE EXPRESSION OF A RULE THAT USED TO HAVE TWO, and the duplication
 * was live: `AnalyticsCanvas.isComposeDropTarget` refused every map with
 * `parsed.display !== 'chart'`, and `buildComposedDisplayVariantCard` refused every map id
 * with its own `parsed.display !== 'chart'`, each carrying its own paragraph explaining
 * why. Both paragraphs described a world in which `ScansByLocation` took a single
 * `dataType` and no second metric was mappable at all — a premise `MAP_RENDERABLE` had
 * already outlived by the time anyone read them. Both call sites now ask here.
 *
 * `null` means "no refusal from this rule" — it does NOT mean the drop is accepted, only
 * that this particular rule has nothing to say. The caller still applies the ordinary
 * capability and duplicate-series gates.
 *
 * ## The two sentences, and why the distinction is worth the record
 *
 * A refusal that cannot say what is missing is a dead control wearing an explanation
 * (§46). So the reason depends on whether ANY grouping could draw the metric:
 *
 *   - NO grouping can  -> a fact about the data, permanent, and the reader can stop
 *                          trying. `Resales` will never be a place.
 *   - SOME grouping can -> a fact about OUR build state, ours to close, and the reader
 *                          should expect it to arrive. `Invalid scans` is a country away.
 *
 * Collapsing them into one sentence would have told a brand chasing counterfeits that
 * invalid scans cannot be mapped, one card away from a choropleth that has been able to
 * count them per country since it shipped. That is the plausible-looking falsehood this
 * file exists to refuse, and it is the reason this returns two sentences rather than a
 * boolean.
 */
export const analyticsComposeTargetRefusal = (
  display: AnalyticsCardCategory,
  metricCardId: string,
): string | null => {
  /*
    The one display that composes, and the SAME fact the builder consumes as a boolean. A
    chart has a shared Y-AXIS, so a second series is another line measured against the same
    scale — position carries the comparison and nothing is given up to make room.
  */
  if (analyticsComposedDisplaySupported(display)) return null

  switch (display) {
    case 'map': {
      /*
        A map holds ONE measure, at either grouping, because its only free channel is the
        mark's COLOUR and colour is already spent. A pin is coloured by its scan OUTCOME
        (invalid red, verified purple) — the signal the anti-counterfeit product exists to
        surface — and a country is coloured by its own value on a sequential scale. There
        is no second channel left, and unlike the chart there is no shared axis to fall
        back on: on a map POSITION IS THE DATA, so two measures do not sit at different
        heights, they sit on top of each other.

        Refused rather than accepted-and-ignored, for the reason every affordance on this
        page is stated before the gesture rather than failed after it.
      */
      const pin = analyticsMapGroupingCapability(metricCardId, 'pin')
      const country = analyticsMapGroupingCapability(metricCardId, 'country')

      /*
        Durable, and about the DATA: NO grouping will ever place this, so hand back a
        capability sentence rather than inventing a second one. The reader can stop trying.

        ⚠️ WHICH sentence is not obvious, and taking the pin's unconditionally was wrong.
        The reasoning now lives on `mapRefusalReason`, because the render pass gave it a
        SECOND caller (`analyticsMapCapability`, answering the same question for the
        `Display as` chip) and two copies of a subtle pick is how one of them comes to
        serve the wrong sentence.
      */
      if (!pin.capable && !country.capable) {
        return mapRefusalReason(metricCardId, pin, country)
      }
      // Mappable — just not TOGETHER. About our build state, not about the data, and the
      // reader should expect a map of its own to work.
      return 'This map already shows one measure, and the colour is that measure. Add this as its own map instead.'
    }

    /*
      A table is a single ranked breakdown: its rows ARE one metric's distribution, and a
      second metric would need either a second ranking (two orderings, one row set) or a
      second column set. Neither is built, and which one is right is a product question
      rather than a missing branch.
    */
    case 'table':
      return 'A table ranks one measure at a time. Add this as its own table instead.'

    /*
      A tile is not a variant target at all — `isComposeDropTarget` rejects a non-panel
      before it reaches here. Answered rather than defaulted, so the switch stays total
      and a new category is a compile error.
    */
    case 'metric':
      return 'A single figure has nothing for a second measure to join.'

    /* Returned above. Listed so the switch stays TOTAL — a new card category is a compile
       error here rather than a target that silently refuses with `undefined`. */
    case 'chart':
      return null
  }
}
